import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import "./SessionIpadView.css";
import api from "./api";
import { useDialog } from './useDialog';
import { useSessionManager } from '../context/SessionManagerContext';
import { useSocket } from '../context/SocketContext';
import useAuth from "../hooks/useAuth";
import EscalationModal from './EscalationModal';
import SessionVideoPlayer from './SessionVideoPlayer';
import DualRings from './DualRings';
import { ROM_PARTS, MUSCLE_PARTS } from './RomAssessment';

/* ─── Derive side from BPConfig key suffix ─── */
function deriveSide(bpKey) {
  if (bpKey.endsWith('_LB'))      return 'left';
  if (bpKey.endsWith('_RB'))      return 'right';
  if (bpKey.endsWith('_L'))       return 'left';
  if (bpKey.endsWith('_R'))       return 'right';
  return null;
}

// Build MUSCLE_LOOKUP from muscleParts array
function buildMuscleLookup(parts) {
  const lookup = {};
  (parts || []).forEach(m => { lookup[m.key] = m; });
  return lookup;
}

/* ─── Helpers ─────────────────────────────────────────────────── */
function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

/** Normalize duration to seconds.
 *  - If durationUnit is explicitly 'seconds' (or missing), value is already in seconds.
 *  - If durationUnit is explicitly 'minutes', multiply by 60. */
function normalizeDur(raw, unit) {
  const d = Number(raw) || 0;
  if (d <= 0) return 60;
  if (unit === 'minutes') return d * 60;
  // Default: treat as seconds (modern data + legacy data both store seconds)
  return d;
}

/** Format a duration stored in SECONDS into a human-readable string */
function fmtDur(sec) {
  const t = Number(sec) || 0;
  if (t < 60) return `${t}s`;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  if (h > 0) return s > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${h}h ${m}m` : `${h}h`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/* ─── EXERCISE VARIANTS ──────────────────────────────────────── */
const VARIANTS = ['Standard', 'Left', 'Right', 'Rotation-Left', 'Rotation-Right', 'Bilateral'];

/* ─── Per-exercise segment colours for DualRings outer ring ─── */
const SEGMENT_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4',
  '#f97316','#ec4899','#6366f1','#14b8a6','#eab308','#a855f7',
];

/* ─── ADJUSTMENT OPTIONS (static labels, dynamic checked state) ── */
const ADJUSTMENT_SECTIONS = [
  {
    title: "Position Modifications",
    grid: true,
    left: [
      "Extra pillow under knee",
      "Bolster support",
      "Wall support",
    ],
    right: [
      "Extra pillow under lumbar",
      "Strap support",
      "Chair assisted",
    ],
  },
  {
    title: "Range Modifications",
    left: [
      "Reduced ROM",
      "Assisted ROM",
      "Partial hold only",
      "Isometric instead of dynamic",
    ],
  },
  {
    title: "Load Modifications",
    left: [
      "Reduced hold time",
      "Increased hold time",
      "Reduced reps",
      "Added resistance",
    ],
  },
  {
    title: "Pain-Based Modifications",
    left: [
      "Stopped due to pain",
      "Modified due to discomfort",
      "Muscle fatigue early",
    ],
  },
];

/* ─── Per-body-part helpers ──────────────────────────────────── */
const SIV_PART_LABELS = {
  cervical: "Cervical/Neck", upperback: "Upper Back", middleback: "Middle Back",
  lowerback: "Lower Back", heart: "Heart", digestive: "Digestive",
  abdomen: "Abdomen", renal: "Renal", reproductive: "Reproductive",
  coccyx: "Coccyx", chest: "Chest", lung: "Lungs",
  shoulder_L: "Shoulder (L)", shoulder_R: "Shoulder (R)",
  shoulder_LB: "Shoulder (L)", shoulder_RB: "Shoulder (R)",
  arm_L: "Arm (L)", arm_R: "Arm (R)", wrist_L: "Wrist (L)", wrist_R: "Wrist (R)",
  knee_L: "Knee (L)", knee_R: "Knee (R)", thigh_L: "Thigh (L)", thigh_R: "Thigh (R)",
  leg_L: "Leg (L)", leg_R: "Leg (R)", calf_L: "Calf (L)", calf_R: "Calf (R)",
  ankle_L: "Ankle (L)", ankle_R: "Ankle (R)", foot_L: "Foot (L)", foot_R: "Foot (R)",
};

/** Maps body-part key → stype string stored in sequences (must match ConsultationIpadView PART_TO_STYPE) */
const SIV_PART_TO_STYPE = {
  cervical: "Cervical (Neck)", upperback: "UpperBack", middleback: "MiddleBack",
  lowerback: "LowerBack & Pelvis", chest: "Chest", heart: "Heart",
  digestive: "Digestive", abdomen: "Abdomen", renal: "Renal",
  reproductive: "Reproductive", coccyx: "Coccyx", lung: "Lungs",
  shoulder_L: "Shoulder", shoulder_R: "Shoulder", shoulder_LB: "Shoulder", shoulder_RB: "Shoulder",
  arm_L: "Arm", arm_R: "Arm", wrist_L: "Wrist", wrist_R: "Wrist",
  knee_L: "Knee", knee_R: "Knee", thigh_L: "Thigh", thigh_R: "Thigh",
  leg_L: "Leg", leg_R: "Leg", calf_L: "Calf", calf_R: "Calf",
  ankle_L: "Ankle", ankle_R: "Ankle", foot_L: "Foot", foot_R: "Foot",
};

function extractBodyPartsForSession(cons) {
  if (!cons) return [];
  const parts = [];
  const seen = new Set();
  const single = ["lowerback","middleback","upperback","cervical","heart","digestive","abdomen","renal","reproductive"];
  single.forEach(part => {
    if (cons[part]?.grade && !seen.has(part)) {
      seen.add(part);
      parts.push({ key: part, label: SIV_PART_LABELS[part] || part });
    }
  });
  const arrayParts = ["knee","shoulder","leg","ankle","foot","thigh","arm","wrist","calf","chest","coccyx","lung"];
  arrayParts.forEach(part => {
    if (!Array.isArray(cons[part])) return;
    cons[part].forEach(entry => {
      if (!entry?.grade) return;
      const side = entry.painside === "left" ? "_L" : "_R";
      const key = part + side;
      if (!seen.has(key)) {
        seen.add(key);
        parts.push({ key, label: SIV_PART_LABELS[key] || key });
      }
    });
  });
  return parts;
}

/** Filter body parts to only those that have at least one sequence targeting that region */
function filterPartsBySequences(parts, seqs) {
  if (!seqs || seqs.length === 0) return parts; // no sequences → show all assessed parts
  // Build a set of stype strings (lowercased) present in the sequences
  const activeStypes = new Set(
    seqs.flatMap(s => Array.isArray(s.stype) ? s.stype : (s.stype ? [s.stype] : []))
       .map(st => (typeof st === 'string' ? st.toLowerCase() : ''))
       .filter(Boolean)
  );
  return parts.filter(({ key }) => {
    const stype = SIV_PART_TO_STYPE[key];
    return stype && activeStypes.has(stype.toLowerCase());
  });
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function SessionIpadView({ patientCard: propCard }) {
  const { patientCard: pcParam } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  // Appointment passed via navigation state (e.g. from SessionTabStrip for old stuck sessions)
  const resumeApptRef = useRef(location?.state?.resumeAppt || null);
  // When embedded as a tab in PatientDetails, propCard is passed directly.
  // When accessed via /SessionIpadView/:patientCard route, pcParam is used.

  /* ── Search state (shown when no patientCard in URL) ─────── */
  const [searchInput, setSearchInput]     = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);
  const debounceRef = useRef(null);
  const { dialogNode, showPrompt, showAlert } = useDialog();
  const sm = useSessionManager();

  /* ── Data state ───────────────────────────────────────────── */
  const [patientCard, setPatientCard]   = useState(propCard || pcParam || "");

  // ── Context is the SINGLE source of truth for timer/pause/ding state ─────
  // Derived every render — no local useState duplication. All mutations go
  // through sm.update(patientCard, {...}) so every subscriber stays in sync.
  const ctx        = sm?.sessions?.[patientCard] ?? null;
  const isPaused   = ctx?.isPaused   ?? true;
  const elapsedSec = ctx?.elapsedSec ?? 0;
  const isDinging  = ctx?.isDinging  ?? false;
  // Explicit state required for multi-client flow:
  // timer complete -> buzzer/waiting -> trainer acknowledges -> trainer resumes manually.
  const isWaitingForResume = ctx?.waitingForResume ?? false;
  const [profile, setProfile]           = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [appointment, setAppointment]   = useState(null);
  const [allSessions, setAllSessions]   = useState([]);
  const [exercises, setExercises]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  /* ── Session state ────────────────────────────────────────── */
  const [activeIdx, setActiveIdx]           = useState(0);
  const [showAdjustments, setShowAdjustments] = useState(false);
  // Adjustments are stored per-exercise in exercises[i].adjustments
  // Helper to get/set current exercise's adjustments
  const currentAdjustments = (exercises[activeIdx]?.adjustments) || {};
  const setCurrentAdjustments = useCallback((updater) => {
    setExercises(prev => prev.map((ex, i) => {
      if (i !== activeIdx) return ex;
      const newAdj = typeof updater === 'function' ? updater(ex.adjustments || {}) : updater;
      return { ...ex, adjustments: newAdj };
    }));
  }, [activeIdx]);
  const [mvc, setMvc]   = useState(0);
  const [rom, setRom]   = useState(0);
  const [pain, setPain] = useState(0);
  const [romByPart, setRomByPart]   = useState({});  // { partKey: { moveKey: { active, passive, pain } } }
  const [mvcByPart, setMvcByPart]   = useState({});  // { partKey: { grpKey: { grade } } }
  const [painByPart, setPainByPart] = useState({});  // { partKey: 0-10 }
  const [sessionParts, setSessionParts] = useState([]); // [{ key, label, metricType?, isLinkedFrom? }] from consultation
  const [bpConfig, setBpConfig] = useState({ front: [], back: [] });
  const [clinicalMuscleParts, setClinicalMuscleParts] = useState(MUSCLE_PARTS);
  const [clinicalRomParts, setClinicalRomParts] = useState(ROM_PARTS);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [editExIdx, setEditExIdx]               = useState(null);
  const [showAddExercise, setShowAddExercise]   = useState(false);
  const [showSkippedRestart, setShowSkippedRestart] = useState(false);
  const [consultationAllSeqs, setConsultationAllSeqs] = useState([]);
  const [creatingAppt, setCreatingAppt] = useState(false);
  const [sessionRecomms, setSessionRecomms] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showRecommPrompt, setShowRecommPrompt] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [historySession, setHistorySession] = useState(null); // appointment object to show in history modal
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [zeroPartsOpen, setZeroPartsOpen] = useState(false);
  const [romMvcTab, setRomMvcTab] = useState('rom');
  const [romNonZeroOpen, setRomNonZeroOpen] = useState(false);
  const [mvcNonZeroOpen, setMvcNonZeroOpen] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoPopped,    setVideoPopped]    = useState(false);  // true = floating PiP, false = embedded in timer card
  const [segments,       setSegments]       = useState([]);     // DualRings outer ring segments
  const [showNextApptModal, setShowNextApptModal] = useState(false);
  const [nextApptDate,      setNextApptDate]      = useState('');
  const [consultUpdate, setConsultUpdate] = useState(null); // { reviewedBy, date, added, addedRecs } if consultation changed since last session
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTargetIdx, setDuplicateTargetIdx] = useState(null);

  const { socketRef }  = useSocket() || {};
  const intervalRef    = useRef(null);
  const dragIdxRef     = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const audioRef       = useRef(null);
  const soundLoopRef   = useRef(null);
  const videoPlayerRef = useRef(null); // ref to SessionVideoPlayer (pause/play)
  // When context auto-pauses (isDinging) while SIV is not mounted, we need to call
  // markExerciseDone once exercises + appointment have loaded on navigate-back.
  const pendingDingIdxRef = useRef(null);

  const autoCompleteFiredRef = useRef(false);

  // Reset per-patient refs whenever the viewed patient changes — prevents stale
  // exercise-completion state from patient A bleeding into patient B's session.
  useEffect(() => {
    autoCompleteFiredRef.current = false;
    pendingDingIdxRef.current = null;
  }, [patientCard]); // eslint-disable-line

  /* ── Fire markExerciseDone after navigate-back when context already dinging ── */
  // IMPORTANT: declared BEFORE auto-complete effect so React executes it first when both
  // fire on the same render (e.g., exercises load AND elapsedSec syncs to dur simultaneously).
  // Sets autoCompleteFiredRef.current = true to block auto-complete from double-calling.
  useEffect(() => {
    if (pendingDingIdxRef.current === null || !appointment || !exercises.length) return;
    const idx = pendingDingIdxRef.current;
    pendingDingIdxRef.current = null;
    // Do NOT gate on ctx.isDinging — acknowledgeCard() (called from tab strip tap)
    // can clear isDinging before we get here, but autoStoppedIdx is still set.
    const ctxEx = exercises[idx];
    if (!ctxEx || ctxEx.status === 'Completed' || ctxEx.status === 'Skipped') {
      // Already done in backend data — clear ding and let user resume manually
      sm?.update(patientCard, { isDinging: false, autoStoppedIdx: null });
      return;
    }
    // Use the component-scope ctx (derived from sm.sessions[patientCard])
    autoCompleteFiredRef.current = true; // block auto-complete from double-firing on same render
    const dur = Number(ctxEx.duration) || ctx?.elapsedSec || 1;
    markExerciseDone(idx, dur, { fromTimer: true });
  }, [appointment, exercises]); // eslint-disable-line

  /* ── Auto-complete rep when elapsed reaches exercise duration ── */
  // NOTE: isPaused guard removed — context may have set isPaused:true simultaneously
  // with setting elapsedSec = dur; local isPaused lags one render so this still fires.
  useEffect(() => {
    // Reset guard when timer is zeroed (next rep / manual done)
    if (elapsedSec === 0) { autoCompleteFiredRef.current = false; return; }
    if (autoCompleteFiredRef.current) return; // prevent double-fire within same rep or navigate-back
    if (appointment?.status !== 'inprogress') return;
    const activeEx = exercises[activeIdx];
    if (!activeEx || activeEx.status === 'Completed' || activeEx.status === 'Skipped' || activeEx.held) return;
    const dur = Number(activeEx.duration) || 0;
    if (dur > 0 && elapsedSec >= dur) {
      autoCompleteFiredRef.current = true;
      markExerciseDone(activeIdx, dur, { fromTimer: true }); // pass capped duration; completion path is allowed while timer auto-stops
    }
  }, [elapsedSec]); // eslint-disable-line

  // Push exercises + activeIdx → context so PiP cards stay up to date
  useEffect(() => {
    if (patientCard && sm?.sessions?.[patientCard]) {
      sm.update(patientCard, { exercises, activeIdx });
    }
  }, [exercises, activeIdx, patientCard]); // eslint-disable-line

  // ── Auto-pause when ALL exercises are done (timer must stop at end of workout) ──
  // NOTE: Do NOT clear isDinging here — let the buzzer ring at workout end.
  // The user stops it via the stop-buzzer button, or markExerciseDone handles it.
  useEffect(() => {
    if (!exercises.length || !appointment || appointment.status !== 'inprogress') return;
    const allDone = exercises.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);
    if (allDone && patientCard && sm?.sessions?.[patientCard] && !sm.sessions[patientCard].isPaused) {
      sm.update(patientCard, {
        isPaused: true,
        // isDinging left as-is so buzzer keeps ringing until user stops it
        waitingForResume: false,
        pauseReason: 'workoutComplete',
        autoStoppedIdx: null,
        finishedAt: Date.now()
      });
    }
  }, [exercises]); // eslint-disable-line

  // ── Derive DualRings outer segments from completed exercises ──────────────
  // One coloured arc per completed exercise, width proportional to its share of total workout time.
  // Purely additive — does not modify any existing state or logic.
  useEffect(() => {
    const totalSec = exercises.filter(e => !e.held).reduce(
      (s, e) => s + (Number(e.duration) || 1) * (Number(e.repetition) || 1), 0
    ) || 1;
    const segs = exercises
      .map((e, i) => {
        if (e.held || e.status !== 'Completed') return null;
        const exSec = (Number(e.duration) || 1) * (Number(e.repetition) || 1);
        return { progress: Math.min(100, (exSec / totalSec) * 100), color: SEGMENT_COLORS[i % SEGMENT_COLORS.length] };
      })
      .filter(Boolean);
    setSegments(segs);
  }, [exercises]); // eslint-disable-line

  /* ── Persist session state locally (for refresh restore) ── */
  // Only depends on exercises/activeIdx changes (not every timer tick) — elapsedSec
  // is read live from context at save time so the snapshot is still fresh.
  useEffect(() => {
    if (!appointment?._id || appointment.status !== 'inprogress' || exercises.length === 0) return;
    const key = `siv-${patientCard}-${appointment._id}`;
    try {
      localStorage.setItem(key, JSON.stringify({
        exercises, activeIdx,
        elapsedSec: ctx?.elapsedSec ?? 0,
        savedAt:    Date.now(),
        wasPaused:  ctx?.isPaused ?? true,
        isDinging:  ctx?.isDinging ?? false,
        waitingForResume: ctx?.waitingForResume ?? false,
        pauseReason: ctx?.pauseReason ?? null,
        autoStoppedIdx: ctx?.autoStoppedIdx ?? null,
        dingAt:     ctx?.dingAt ?? null,
      }));
    } catch (e) {}
  }, [
    exercises, activeIdx, appointment, patientCard,
    ctx?.elapsedSec, ctx?.isPaused, ctx?.isDinging, ctx?.waitingForResume,
    ctx?.pauseReason, ctx?.autoStoppedIdx, ctx?.dingAt
  ]); // eslint-disable-line

  /* ── Socket state sync — emits every 10s to keep other devices current ── */
  // Every 6th emit (60s) includes persist:true to save to MongoDB.
  // Always reads liveCtx fresh inside the interval — no stale closure on exercises/isPaused.
  useEffect(() => {
    if (!appointment?._id || appointment.status !== 'inprogress') return;
    let count = 0;
    const id = setInterval(() => {
      count++;
      const liveCtx = sm?.sessions?.[patientCard];
      socketRef?.current?.emit('session:state', {
        patientCard,
        elapsedSec:     liveCtx?.elapsedSec     ?? 0,
        activeIdx:      liveCtx?.activeIdx      ?? activeIdx,
        isPaused:       liveCtx?.isPaused       ?? true,
        isDinging:      liveCtx?.isDinging      ?? false,
        autoStoppedIdx: liveCtx?.autoStoppedIdx ?? null,
        waitingForResume: liveCtx?.waitingForResume ?? false,
        pauseReason:    liveCtx?.pauseReason    ?? null,
        exercises:      liveCtx?.exercises      ?? exercises,
        persist: count % 6 === 0,
      });
    }, 10_000);
    return () => clearInterval(id);
  }, [appointment, patientCard]); // eslint-disable-line

  /* ── Audio helpers (mirrors ExerciseTimerWithList pattern) ── */
  const unlockAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.play()
      .then(() => { audioRef.current.pause(); audioRef.current.currentTime = 0; })
      .catch(() => {});
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
    if (soundLoopRef.current) { clearTimeout(soundLoopRef.current); soundLoopRef.current = null; }
  };

  // Emit full session snapshot to other devices — reusable for any state change that needs instant sync
  const emitFullState = (overrides = {}, persist = false) => {
    const liveCtx = sm?.sessions?.[patientCard];
    if (!liveCtx) return;
    socketRef?.current?.emit('session:state', {
      patientCard,
      elapsedSec:     liveCtx.elapsedSec     ?? 0,
      activeIdx:      liveCtx.activeIdx      ?? 0,
      isPaused:       liveCtx.isPaused       ?? true,
      isDinging:      liveCtx.isDinging      ?? false,
      autoStoppedIdx: liveCtx.autoStoppedIdx ?? null,
      waitingForResume: liveCtx.waitingForResume ?? false,
      pauseReason:    liveCtx.pauseReason    ?? null,
      exercises:      liveCtx.exercises      ?? exercises,
      persist,
      ...overrides,
    });
  };

  // Ding on rep/exercise completion — loops until therapist explicitly taps this timer.
  // Important for parallel clients: no global document tap and no 30s auto-dismiss,
  // otherwise a tap on Client B can accidentally silence Client A.
  const playDing = () => {
    sm?.update(patientCard, { isDinging: true }); // vibrate the session card/footer tab
    videoPlayerRef.current?.pause(); // pause PiP video when exercise dings
    if (!audioRef.current) return;
    stopAudio();
    audioRef.current.currentTime = 0;
    audioRef.current.volume = 1;
    audioRef.current.muted = false;
    audioRef.current.loop = true;
    audioRef.current.play().catch(() => {});
  };

  const acknowledgeDing = () => {
    stopAudio();
    sm?.update(patientCard, { isDinging: false });
    emitFullState({ isDinging: false });
  };

  // If the trainer opens a client whose timer had completed while it was in the
  // footer/background, restart the local buzzer for this mounted screen.
  useEffect(() => {
    if (!isDinging || !appointment || appointment.status !== 'inprogress') return;
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.volume = 1;
    audioRef.current.muted = false;
    audioRef.current.loop = true;
    audioRef.current.play().catch(() => {});
    return () => stopAudio();
  }, [isDinging, appointment?.status]); // eslint-disable-line

  /* ── Auto-load from prop (tab embed) or URL param (route) ─── */
  useEffect(() => {
    if (propCard) {
      setPatientCard(propCard);
      loadPatientData(propCard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCard]);

  useEffect(() => {
    if (pcParam && !propCard) {
      setPatientCard(pcParam);
      loadPatientData(pcParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcParam]);

  /* ── Debounced search ─────────────────────────────────────── */
  useEffect(() => {
    if (searchInput.length < 3) { setSearchResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await api.get(
          `${process.env.REACT_APP_URL}/patients/profiles/search/${searchInput}`
        );
        setSearchResults(res.data || []);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  /* ── Load all patient data ────────────────────────────────── */
  const loadPatientData = async (card) => {
    setLoading(true);
    setError("");
    // ── Reset local display state for new patient load ───────────
    // isPaused and elapsedSec are derived from context — no reset needed here.
    stopAudio();
    setExercises([]);
    setActiveIdx(0);
    // adjustments now live per-exercise in exercises[i].adjustments — cleared with setExercises([])
    setMvc(0);
    setRom(0);
    setPain(0);
    setRomByPart({});
    setMvcByPart({});
    setPainByPart({});
    setSessionParts([]);
    setShowRecommendations(false);
    setShowAdjustments(false);
    setAppointment(null);
    // ────────────────────────────────────────────────────────────
    try {
      const [profileRes, consultRes, apptRes, allSessionsRes, bpConfigRes, romAssessRes, clinConfigRes] = await Promise.all([
        api.get(`${process.env.REACT_APP_URL}/patients/profiles/${card}`),
        api.get(`${process.env.REACT_APP_URL}/patients/consultation/${card}`),
        api.get(
          `${process.env.REACT_APP_URL}/appointments/patient/${card}/today/upcoming/10?_=${Date.now()}`
        ),
        api.get(
          `${process.env.REACT_APP_URL}/appointments/type/Therapy/patient/${card}`
        ),
        api.get(`${process.env.REACT_APP_URL}/bodyparts`).catch(() => ({ data: null })),
        api.get(`${process.env.REACT_APP_URL}/patients/rom/${card}`).catch(() => ({ data: null })),
        api.get(`${process.env.REACT_APP_URL}/bodyparts/clinical`).catch(() => ({ data: null })),
      ]);

      setProfile(profileRes.data);
      setConsultation(consultRes.data);
      const bpData = bpConfigRes.data;
      if (bpData) setBpConfig({ front: bpData.front || [], back: bpData.back || [] });
      // Load ClinicalConfig for ROM/muscle definitions
      if (clinConfigRes?.data?.romParts?.length > 0) setClinicalRomParts(clinConfigRes.data.romParts);
      if (clinConfigRes?.data?.muscleParts?.length > 0) setClinicalMuscleParts(clinConfigRes.data.muscleParts);

      // Pre-fill ROM/MVC/Pain from last completed session (populated after allSessionsRes is read below)

      // Pick best appointment: in-progress first, then scheduled (including missed), then nearby
      const apptResults = apptRes.data?.resultsUTC || [];
      const nowMs = Date.now();
      let todayAppt;
      const passedAppt = resumeApptRef.current;
      if (passedAppt?._id && passedAppt.status === 'inprogress') {
        const freshVersion = apptResults.find(a => a._id === passedAppt._id);
        todayAppt = freshVersion || passedAppt;
      } else {
        // Also check allSessions for today's scheduled appointments (missed ones outside ±60min window)
        const todayStr = new Date().toISOString().slice(0, 10);
        const allTodayScheduled = (allSessionsRes.data || []).filter(a => {
          const aDate = (a.start || a.date || '').slice(0, 10);
          return a.status === 'scheduled' && aDate === todayStr;
        });
        const merged = new Map();
        [...apptResults, ...allTodayScheduled].forEach(a => { if (a._id && !merged.has(a._id)) merged.set(a._id, a); });
        const all = [...merged.values()];

        const inProgressAppt = all.find(a => a.status === 'inprogress');
        const scheduledAppt = all.find(a => a.status === 'scheduled');
        const nearbyAppt = all.find(a => {
          const startMs = new Date(a.start).getTime();
          return Math.abs(startMs - nowMs) <= 30 * 60 * 1000;
        });
        todayAppt = inProgressAppt || scheduledAppt || nearbyAppt || apptResults[0] || null;
      }
      setAppointment(todayAppt);

      const sessions = allSessionsRes.data || [];
      setAllSessions(sessions);

      // Pre-fill global ROM/MVC/Pain from last completed session
      const sortedDone = [...sessions]
        .filter(s => s.status === 'Completed' && s.sessionFeedback)
        .sort((a, b) => new Date(b.start) - new Date(a.start));
      const lastDone = sortedDone[0];
      const sf = lastDone?.sessionFeedback;
      if (sf?.mvc != null) setMvc(Number(sf.mvc) || 0);
      if (sf?.rom != null) setRom(Number(sf.rom) || 0);
      if (sf?.pain != null) setPain(Number(sf.pain) || 0);
      // Per-part values merged after session parts are built (see below)

      // ── Detect if consultation was updated since last session ──
      const reviewHistory = consultRes.data?.reviewHistory || [];
      if (reviewHistory.length > 0) {
        const latestReview = reviewHistory[reviewHistory.length - 1]; // newest at end (stored asc)
        const lastCompletedSession = sessions
          .filter(s => s.status === 'Completed')
          .sort((a, b) => new Date(b.start) - new Date(a.start))[0];
        const lastSessionDate = lastCompletedSession ? new Date(lastCompletedSession.start) : null;
        const reviewDate = new Date(latestReview.date);
        if (!lastSessionDate || reviewDate > lastSessionDate) {
          const ch = latestReview.changes || {};
          const added    = ch.sequencesAdded    || [];
          const removed  = ch.sequencesRemoved  || [];
          const addedR   = ch.recommendationsAdded || [];
          const removedR = ch.recommendationsRemoved || [];
          const hasChanges = added.length || removed.length || addedR.length || removedR.length ||
                             (ch.bodyPartsChanged || []).length;
          if (hasChanges || latestReview.reviewType === 'plan_submitted') {
            setConsultUpdate({
              reviewedBy: latestReview.reviewedBy,
              date:       reviewDate,
              type:       latestReview.reviewType,
              seqAdded:   added,
              seqRemoved: removed,
              recAdded:   addedR,
              recRemoved: removedR,
            });
          }
        } else {
          setConsultUpdate(null);
        }
      }

      // Build exercises from consultation sequences
      const seqs = consultRes.data?.sequences || [];

      // Body parts: only show parts the doctor selected & saved in patient's ROM Assessment
      const allBp = [...(bpData?.front || []), ...(bpData?.back || [])];
      // Normalise metricType casing (DB may have 'rom', 'Rom', etc.)
      allBp.forEach(a => {
        if (!a.metricType) return;
        const mt = a.metricType.toLowerCase();
        if (mt === 'mvc') a.metricType = 'MVC';
        else if (mt === 'rom') a.metricType = 'ROM';
        else if (mt === 'both') a.metricType = 'both';
        else a.metricType = '';
      });
      const bpLookup = {};
      allBp.forEach(a => { bpLookup[a.key] = a; });
      const romLinkedSel = consultRes.data?.planRomLinkedSelections || {};

      // Saadhak's saved assessment — keys in rom/muscleStrength are the clinical parts
      const patientRom = romAssessRes?.data?.rom || {};
      const patientMvc = romAssessRes?.data?.muscleStrength || {};
      const savedClinicalRom = new Set(Object.keys(patientRom).filter(k => !k.startsWith('_')));
      const savedClinicalMvc = new Set(Object.keys(patientMvc).filter(k => !k.startsWith('_')));

      // Use selectedBpKeys saved from LabReadings for precise filtering
      const savedBpKeys = new Set(romAssessRes?.data?.selectedBpKeys || []);

      // Filter BPConfig parts to only those specifically selected in LabReadings
      const enriched = allBp
        .filter(a => a.metricType === 'ROM' || a.metricType === 'MVC' || a.metricType === 'both')
        .filter(a => {
          // If selectedBpKeys are saved, use them for exact match
          if (savedBpKeys.size > 0) return savedBpKeys.has(a.key);
          // Fallback to old clinical key matching (for data saved before this update)
          const clinicalKey = a.romKey || '';
          const mt = a.metricType;
          if ((mt === 'ROM' || mt === 'both') && clinicalKey && savedClinicalRom.has(clinicalKey)) return true;
          if ((mt === 'MVC' || mt === 'both') && (savedClinicalMvc.has(a.key) || (clinicalKey && savedClinicalMvc.has(clinicalKey)))) return true;
          return false;
        })
        .map(a => ({ key: a.key, label: a.label || a.key, metricType: a.metricType, romKey: a.romKey || '', muscles: a.muscles || [] }));

      // Add linked ROM parts selected in GTP (not already present)
      const existingKeys = new Set(enriched.map(p => p.key));
      Object.entries(romLinkedSel).forEach(([parentKey, linkedKeys]) => {
        (linkedKeys || []).forEach(lk => {
          if (!existingKeys.has(lk)) {
            existingKeys.add(lk);
            enriched.push({
              key: lk,
              label: bpLookup[lk]?.label || lk,
              metricType: 'ROM',
              isLinkedFrom: parentKey,
            });
          }
        });
      });

      // No fallback: if patient has no selected BP in LabReadings, show no ROM/MVC tables

      setSessionParts(enriched);

      // ── Per-part pre-fill: ROM Assessment as baseline, last-session values override ──
      // Extract highest active ROM per body part from the latest dedicated assessment
      const romAssessData = romAssessRes?.data;
      // Per-part pre-fill: extract movement-level ROM data from latest assessment
      const assessRomByPart = {};
      if (romAssessData?.rom && typeof romAssessData.rom === 'object') {
        enriched.forEach(({ key, metricType, romKey }) => {
          if (metricType !== 'ROM') return;
          const clinicalKey = romKey || '';
          const side = deriveSide(key);
          const partData = clinicalKey ? romAssessData.rom[clinicalKey] : null;
          if (!partData || typeof partData !== 'object') return;
          const moveMap = {};
          Object.entries(partData).forEach(([moveKey, mv]) => {
            if (!mv || typeof mv !== 'object') return;
            const sd = side ? mv[side] : mv;
            if (sd?.active != null || sd?.passive != null) {
              moveMap[moveKey] = { active: sd?.active, passive: sd?.passive, pain: sd?.pain };
            }
          });
          if (Object.keys(moveMap).length > 0) assessRomByPart[key] = moveMap;
        });
      }
      // Session values take priority (if they're movement-level objects); assessment fills the rest
      const sessionRomByPart = sf?.romByPart || {};
      const finalRomByPart = { ...assessRomByPart };
      Object.entries(sessionRomByPart).forEach(([k, v]) => {
        // Only use session value if it's movement-level (object), skip old flat numbers
        if (v && typeof v === 'object' && !Array.isArray(v)) finalRomByPart[k] = v;
      });
      if (Object.keys(finalRomByPart).length > 0) setRomByPart(finalRomByPart);
      if (sf?.mvcByPart  && Object.keys(sf.mvcByPart).length  > 0) setMvcByPart(sf.mvcByPart);
      if (sf?.painByPart && Object.keys(sf.painByPart).length > 0) setPainByPart(sf.painByPart);

      const isResuming = todayAppt?.status === 'inprogress';

      // Try localStorage snapshot first (preserves timer & activeIdx across refresh)
      let snapshot = null;
      if (isResuming && todayAppt?._id) {
        try { snapshot = JSON.parse(localStorage.getItem(`siv-${card}-${todayAppt._id}`) || 'null'); } catch (e) {}
      }

      let mapped;
      if (snapshot?.exercises?.length) {
        // Restore from local snapshot — merge with any BE status changes
        mapped = snapshot.exercises.map((snap, i) => {
          const beSeq = seqs.find(s => s.name === snap.name) || {};
          return {
            ...beSeq,
            ...snap,
            duration: normalizeDur(beSeq.duration || snap.duration, beSeq.durationUnit || snap.durationUnit),
            durationUnit: 'seconds', // after normalization, always in seconds
            _localId: snap._localId || ((snap.name || "ex") + "-" + i),
          };
        });
      } else {
        // Build from BE sequences: preserve status when resuming so other logins see live state
        mapped = seqs.map((s, i) => ({
          ...s,
          duration: normalizeDur(s.duration, s.durationUnit),
          durationUnit: 'seconds', // after normalization, always in seconds
          _localId: (s.name || "ex") + "-" + i,
          status: isResuming ? (s.status || "notStarted") : "notStarted",
          pendingreps: isResuming
            ? (s.pendingreps != null ? s.pendingreps : (s.repetition || 1))
            : (s.repetition || 1),
        }));
      }

      setExercises(mapped);
      setConsultationAllSeqs(seqs.map(function(s) {
        return Object.assign({}, s, {
          duration: normalizeDur(s.duration, s.durationUnit),
          durationUnit: 'seconds'
        });
      }));
      // Normalise: Recommendation model uses `recommendation` field, not `name`
      setSessionRecomms((consultRes.data?.recommendations || []).map(r => ({
        ...r,
        name: r.name || r.recommendation || r.title || 'Recommendation',
        completed: null,
        qty: '',
      })));
      // Auto-expand recommendations panel if items exist
      if ((consultRes.data?.recommendations || []).length > 0) {
        setShowRecommendations(true);
      }

      // Set active to first non-completed exercise (or snapshot index)
      if (snapshot?.activeIdx != null) {
        setActiveIdx(snapshot.activeIdx);
      } else {
        const firstActive = mapped.findIndex(
          (s) => s.status !== "Completed" && s.status !== "Skipped" && !s.held
        );
        setActiveIdx(firstActive >= 0 ? firstActive : 0);
      }

      // elapsedSec is derived from context — no local setState needed.
      // Register/update context with snapshot elapsed so the ticker starts from the right value.

      // Case 2 fix: if doctor returns to SIV after all exercises done but session not yet marked
      // complete, auto-reopen the feedback modal so they don't have to hunt for the Complete button.
      if (isResuming) {
        const allExsDone = mapped.length > 0 &&
          mapped.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);
        if (allExsDone) setShowFeedbackModal(true);
      }

      // If already in-progress, show video player and sync context
      if (isResuming) {
        setShowVideoPlayer(true);
        const restoredIdx = snapshot?.activeIdx != null
          ? snapshot.activeIdx
          : Math.max(0, mapped.findIndex(s => s.status !== 'Completed' && s.status !== 'Skipped'));

        // If session is already in context (navigating back), preserve the live elapsedSec —
        // calling register() would reset it to the stale snapshot and make the timer appear to jump back.
        // Also preserve dinging state: if context auto-paused while we were away, don't resume yet.
        const alreadyInCtx = !!sm?.sessions?.[card];
        const ctxSession   = alreadyInCtx ? sm.sessions[card] : null;
        // Use autoStoppedIdx as the authoritative "pending ding" signal —
        // acknowledgeCard() (called when user taps the tab strip) can clear
        // isDinging before we reach this code, but autoStoppedIdx persists
        // until markExerciseDone explicitly nullifies it.
        const ctxDinging   = alreadyInCtx && (!!ctxSession?.isDinging || ctxSession?.autoStoppedIdx != null);
        if (ctxDinging) {
          // Exercise timed out while navigating away — record which one so the
          // pendingDing effect can call markExerciseDone once exercises load.
          // isPaused is already true in context (set by the ticker's ding logic).
          const autoIdx = ctxSession.autoStoppedIdx;
          pendingDingIdxRef.current = autoIdx != null ? autoIdx : null;

          // Requirement fix: never bulk-complete future reps/exercises while the
          // therapist is away. Background waiting time is not workout time.
          // The client must stay in waitingForResume until the trainer opens
          // this session, acknowledges the buzzer, prepares the client, then resumes.
        }
        if (alreadyInCtx) {
          // Bug 3 fix: use the LIVE context activeIdx (not the stale localStorage restoredIdx)
          // so navigating back never overwrites the in-progress exercise position.
          const liveActiveIdx = ctxSession.activeIdx ?? restoredIdx;
          setActiveIdx(liveActiveIdx); // sync local state to match what context already has
          // Re-read context FRESH at update time (not the stale ctxSession captured before async gap)
          // to avoid overriding isPaused:true that the context ticker may have set while API calls ran.
          const freshCtx     = sm?.sessions?.[card];
          const freshDinging = !!(freshCtx?.isDinging || freshCtx?.autoStoppedIdx != null);
          // Also catch any ding that started during the async API gap (was missed at top of loadPatientData)
          if (freshDinging && pendingDingIdxRef.current === null) {
            pendingDingIdxRef.current = freshCtx?.autoStoppedIdx ?? null;
          }
          sm?.update(card, {
            patientName:  profileRes.data?.name || card,
            patientPhoto: profileRes.data?.pic  || null,
            exercises:    mapped,
            activeIdx:    liveActiveIdx,
            appointment:  todayAppt,
            // Only force isPaused:true when dinging — otherwise leave context's isPaused untouched.
            // NEVER pass isPaused:false here; it would restart a timer that context auto-paused mid-load.
            ...(freshDinging ? { isPaused: true } : {}),
          });
        } else {
          // Context was lost (page refresh / reconnect) — restore from best available elapsed.
          // Adjust snapshot elapsed for wall-clock time that passed while context was gone.
          let elapsedToRestore = snapshot?.elapsedSec || 0;
          if (snapshot?.savedAt && !snapshot?.wasPaused) {
            elapsedToRestore += Math.floor((Date.now() - snapshot.savedAt) / 1000);
          }
          // Use BE-persisted sessionState as an additional floor (saved every 60s via socket)
          const persistedElapsed = todayAppt?.sessionState?.elapsedSec || 0;
          const bestElapsed = Math.max(elapsedToRestore, persistedElapsed);
          sm?.register(card, {
            patientName:  profileRes.data?.name || card,
            patientPhoto: profileRes.data?.pic  || null,
            exercises:    mapped,
            activeIdx:    restoredIdx,
            elapsedSec:   (snapshot?.waitingForResume || snapshot?.isDinging) ? 0 : bestElapsed,
            isPaused:     (snapshot?.waitingForResume || snapshot?.isDinging) ? true : (snapshot?.wasPaused ?? false),
            isDinging:    snapshot?.isDinging ?? false,
            waitingForResume: (snapshot?.waitingForResume || snapshot?.isDinging) ?? false,
            pauseReason:  snapshot?.pauseReason ?? null,
            autoStoppedIdx: snapshot?.autoStoppedIdx ?? null,
            dingAt:       snapshot?.dingAt ?? null,
            appointment:  todayAppt,
          });
        }
      }
    } catch (err) {
      setError(
        "Failed to load saadhak data. " +
          (err.response?.data || err.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Saadhak select from search ───────────────────────────── */
  const handlePatientSelect = (patient) => {
    setSearchInput("");
    setSearchResults([]);
    setPatientCard(patient.patientCard);
    loadPatientData(patient.patientCard);
  };

  const createAppointment = async () => {
    setCreatingAppt(true);
    try {
      const now = new Date();
      const totalSec = consultationAllSeqs.reduce((sum, s) => sum + (Number(s.duration) || 60) * (s.repetition || 1), 0);
      const durationMin = consultationAllSeqs.length > 0 ? Math.max(10, Math.ceil(totalSec / 60)) : 50;
      const newStart = now;
      const newEnd = new Date(now.getTime() + durationMin * 60000);

      // ── Reuse existing same-day scheduled appointment (covers missed slots) ──
      // Fetch ALL today's appointments for this patient (wider window than ±60min)
      const todayStr = now.toISOString().slice(0, 10);
      const dayStart = new Date(todayStr + 'T00:00:00+05:30');
      const dayEnd   = new Date(todayStr + 'T23:59:59+05:30');
      let reused = false;
      try {
        const todayRes = await api.get(
          `${process.env.REACT_APP_URL}/appointments/patient/${patientCard}/today/upcoming/50?_=${Date.now()}`
        );
        const todayAppts = todayRes.data?.resultsUTC || [];
        // Also fetch any scheduled appointments from earlier today that the narrow window missed
        const allTodayRes = await api.get(
          `${process.env.REACT_APP_URL}/appointments/type/Therapy/patient/${patientCard}`
        );
        const allAppts = allTodayRes.data || [];
        const todayScheduled = allAppts.filter(a => {
          const aDate = new Date(a.start || a.date);
          return a.status === 'scheduled' && aDate >= dayStart && aDate <= dayEnd;
        });
        // Merge: prefer from allAppts as it has wider range
        const mergedMap = new Map();
        [...todayAppts, ...todayScheduled].forEach(a => { if (a._id && !mergedMap.has(a._id)) mergedMap.set(a._id, a); });
        const scheduledToday = [...mergedMap.values()].filter(a => a.status === 'scheduled');

        if (scheduledToday.length > 0) {
          // Reuse the first (oldest) scheduled appointment — update its time to now
          const reusable = scheduledToday.sort((a, b) => new Date(a.start) - new Date(b.start))[0];
          await api.post(`${process.env.REACT_APP_URL}/appointments/update/${reusable._id}`, {
            start: newStart,
            end: newEnd,
            date: newStart,
            username: auth?.username || reusable.username,
          });
          reused = true;
        }
      } catch (_) { /* fall through to create new */ }

      if (!reused) {
        await api.post(`${process.env.REACT_APP_URL}/appointments/add`, {
          title: `${profile.name} / ${profile?.number || ''}`,
          username: auth?.username || 'unassigned',
          patientCard,
          name: profile.name,
          start: newStart,
          end: newEnd,
          date: newStart,
          type: 'Therapy',
          status: 'scheduled',
          branch: profile?.branch || '',
          isOnline: false,
        });
      }
      await loadPatientData(patientCard);
    } catch(err) {
      showAlert('Error creating appointment: ' + (err.response?.data || err.message), 'error');
    } finally {
      setCreatingAppt(false);
    }
  };

  /* ── Session controls ─────────────────────────────────────── */
  const handleStartSession = async () => {
    if (!appointment || appointment.status !== "scheduled") return;
    unlockAudio(); // unlock browser audio on first real user gesture
    try {
      await api.post(
        `${process.env.REACT_APP_URL}/appointments/update/${appointment._id}`,
        { status: "inprogress", startTime: new Date(), leadTherapist: auth?.username }
      );
      setAppointment((prev) => ({ ...prev, status: "inprogress" }));
      setShowVideoPlayer(true); // auto-launch PiP video player
      const apptForCtx = { ...appointment, status: 'inprogress' };
      // Register with context so all timers run in background
      sm?.register(patientCard, {
        patientName:  profile?.name || patientCard,
        patientPhoto: profile?.pic  || null,
        exercises,
        activeIdx,
        elapsedSec:   0,
        isPaused:     false,
        isDinging:    false,
        waitingForResume: false,
        pauseReason:  null,
        autoStoppedIdx: null,
        dingAt:       null,
        appointment:  apptForCtx,
      });
      // Notify other devices via socket
      socketRef?.current?.emit('session:register', {
        patientCard,
        patientName:  profile?.name || patientCard,
        patientPhoto: profile?.pic  || null,
        exercises,
        activeIdx,
        elapsedSec:   0,
        isDinging:    false,
        waitingForResume: false,
        pauseReason:  null,
        autoStoppedIdx: null,
        dingAt:       null,
        appointment:  apptForCtx,
      });
    } catch (err) {
      showAlert("Error starting session: " + (err.response?.data || err.message), 'error');
    }
  };

  // Circle click: if dinging -> only stop buzzer; if workout over -> do nothing; else -> normal pause/resume.
  // It intentionally does NOT advance to the next rep/exercise; only Resume starts the prepared next unit.
  const handleCircleClick = () => {
    if (isDinging) {
      acknowledgeDing();
      return;
    }
    const allDone = exercises.length > 0 && exercises.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);
    if (allDone) return;
    // After buzzer is acknowledged, the timer circle must not accidentally resume.
    // The trainer explicitly uses the Resume button after preparing the client.
    if (isWaitingForResume) return;
    handlePause();
  };

  const handlePause = () => {
    const allDone = exercises.length > 0 && exercises.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);
    if (allDone) return;

    // Resume from timer-complete waiting state. This is the only path that starts
    // the next rep/exercise after a completion buzzer.
    if (isWaitingForResume) {
      // Requirement: trainer must acknowledge the buzzer first by touching the timer.
      // If Resume is pressed while the buzzer is still ringing, only silence it;
      // do not start the next rep/exercise yet.
      if (isDinging) {
        acknowledgeDing();
        return;
      }

      const patch = {
        isPaused: false,
        isDinging: false,
        waitingForResume: false,
        pauseReason: null,
        autoStoppedIdx: null,
        dingAt: null,
      };
      stopAudio();
      sm?.update(patientCard, patch);
      emitFullState(patch);
      return;
    }

    const next = !isPaused; // isPaused derived from context
    const patch = next
      ? { isPaused: true, pauseReason: 'manual' }
      : { isPaused: false, isDinging: false, autoStoppedIdx: null, waitingForResume: false, pauseReason: null, dingAt: null };
    sm?.update(patientCard, patch);
    emitFullState(patch);
  };

  const handleComplete = () => {
    if (!appointment) return;
    const unfilled = sessionRecomms.filter(r => r.completed === null || r.completed === undefined);
    if (unfilled.length > 0) {
      setShowRecommPrompt(true);
      setShowRecommendations(true); // auto-expand recommendations panel
    } else {
      setShowFeedbackModal(true);
    }
  };

  // ── Save exercise order/status to BE (called on reorder or completion) ─────
  const saveExercisesToBE = useCallback(async (exList, statusLabel = 'restarted') => {
    if (!patientCard || !appointment) return;
    try {
      await api.post(
        `${process.env.REACT_APP_URL}/patients/consultation/patientsequences/updateremaining/${patientCard}/${statusLabel}`,
        { reorderedExers: exList.map(e => ({ ...e })) }
      );
    } catch (e) {
      console.error('[SessionView] Failed to save exercises to BE:', e.message);
    }
  }, [patientCard, appointment]);

  const handleFeedbackSave = async (feedback) => {
    setShowFeedbackModal(false);
    try {
      // ── Bulk-mark remaining exercises if therapist chose to ──────────────────
      const { bulkMarkStatus, actualStartTime, actualEndTime, leadTherapist: fbLead, leadTherapistDesignation: fbDesignation, helperTherapist1, helperTherapist2, sessionComments, ...scores } = feedback;
      let finalExercises = exercises;
      if (bulkMarkStatus) {
        finalExercises = exercises.map(e =>
          (e.status !== 'Completed' && e.status !== 'Skipped')
            ? { ...e, status: bulkMarkStatus }
            : e
        );
        setExercises(finalExercises);
      }
      // Save final exercise state to consultation
      await saveExercisesToBE(finalExercises, 'Completed');
      // Gather adjustments from ALL exercises (per-exercise adjustments)
      const allAdjLabels = new Set();
      finalExercises.forEach(ex => {
        if (ex.adjustments) {
          Object.entries(ex.adjustments).forEach(([label, v]) => { if (v) allAdjLabels.add(label); });
        }
      });
      const appliedAdjs = Array.from(allAdjLabels);
      const payload = {
        status: "Completed",
        leadTherapist:             fbLead,
        leadTherapistDesignation:  fbDesignation || '',
        helperTherapist1: helperTherapist1 || '',
        helperTherapist2: helperTherapist2 || '',
        sessionComments:  sessionComments,
        sessionFeedback: {
          ...scores,
          mvc,
          rom,
          pain,
          romByPart,
          mvcByPart,
          painByPart,
          adjustmentsApplied: appliedAdjs,
          recommendationTracking: sessionRecomms.map(r => ({
            name: r.name || r.recommendation || r.title || '',
            completed: r.completed,
            qty: r.qty ? Number(r.qty) : null,
          })),
          // Full exercise snapshot — each exercise in order with all results
          exerciseSnapshot: finalExercises.map((ex, i) => ({
            sequence:       i + 1,
            name:           ex.name || '',
            status:         ex.status || 'notStarted',
            duration:       ex.duration || 0,
            durationUnit:   ex.durationUnit || 'seconds',
            repetition:     ex.repetition || 1,
            pendingreps:    ex.pendingreps ?? ex.repetition ?? 1,
            repsCompleted:  ex.repsCompleted ?? 0,
            timeSpentSec:   ex.timeSpentSec ?? 0,
            remarks:        ex.remarks || '',
            commentLog:     Array.isArray(ex.commentLog) ? ex.commentLog : [],
            wasRestarted:   ex.wasRestarted || false,
            adjustments:    Object.entries(ex.adjustments || {}).filter(([,v]) => v).map(([l]) => l),
          })),
        },
      };
      // Include actual start/end times if provided
      if (actualStartTime) payload.actualStartTime = new Date(actualStartTime);
      if (actualEndTime)   payload.actualEndTime   = new Date(actualEndTime);
      await api.post(`${process.env.REACT_APP_URL}/appointments/update/${appointment._id}`, payload);
      setAppointment((prev) => {
        // Clear localStorage snapshot now that session is done
        if (prev?._id) {
          try { localStorage.removeItem(`siv-${patientCard}-${prev._id}`); } catch (e) {}
        }
        return { ...prev, status: "Completed" };
      });
      // isPaused is derived from context — after unregister, ctx = null → isPaused = true automatically
      sm?.unregister(patientCard); // remove from background timer + PiP cards
      socketRef?.current?.emit('session:end', { patientCard });
      // Refresh sessions list to include new feedback data
      const sessRes = await api.get(`${process.env.REACT_APP_URL}/appointments/type/Therapy/patient/${patientCard}`);
      setAllSessions(sessRes.data || []);
      // Pre-fill tomorrow same-time for next appointment scheduling
      const nextDt = new Date(appointment?.start || Date.now());
      nextDt.setDate(nextDt.getDate() + 1);
      const pad = n => String(n).padStart(2, '0');
      setNextApptDate(`${nextDt.getFullYear()}-${pad(nextDt.getMonth() + 1)}-${pad(nextDt.getDate())}T${pad(nextDt.getHours())}:${pad(nextDt.getMinutes())}`);
      setShowNextApptModal(true); // prompt to schedule next appointment
    } catch (err) {
      showAlert("Error completing session: " + (err.response?.data || err.message), 'error');
    }
  };

  const handleEscalate = () => {
    if (!appointment) return;
    setShowEscalationModal(true);
  };

  // ── Schedule next appointment (post-session) ──────────────────────────────
  const scheduleNextAppointment = async (dateTimeStr) => {
    if (!dateTimeStr || !profile) return;
    try {
      const start = new Date(dateTimeStr);
      const totalSec = consultationAllSeqs.reduce((s, e) => s + (Number(e.duration) || 60) * (e.repetition || 1), 0);
      const durationMin = consultationAllSeqs.length > 0 ? Math.max(10, Math.ceil(totalSec / 60)) : 50;
      await api.post(`${process.env.REACT_APP_URL}/appointments/add`, {
        title:      `${profile.name} / ${profile?.number || ''}`,
        username:   appointment?.username || auth?.username || 'unassigned',
        patientCard,
        name:       profile.name,
        start,
        end:        new Date(start.getTime() + durationMin * 60000),
        date:       start,
        type:       'Therapy',
        status:     'scheduled',
        branch:     profile?.branch || '',
        isOnline:   false,
      });
      showAlert('Next appointment scheduled ✅', 'success');
      setShowNextApptModal(false);
    } catch (err) {
      showAlert('Error scheduling appointment: ' + (err.response?.data || err.message), 'error');
    }
  };

  /* ── Exercise controls ────────────────────────────────────── */
  const markExerciseDone = (idx, capturedTime, opts = {}) => {
    if (!appointment || appointment.status !== 'inprogress') return;

    // Manual Done/Skip actions must never consume another rep while the session
    // is already waiting after a timer-completion. Auto-complete/pending-ding
    // paths pass opts.fromTimer=true and are allowed to record the completed unit.
    if (!opts.fromTimer && (isWaitingForResume || isDinging)) return;

    const currentEx = exercises[idx];
    if (!currentEx || currentEx.status === 'Completed' || currentEx.status === 'Skipped' || currentEx.held) return;

    const totalReps = Number(currentEx.repetition) || 1;
    const pending = currentEx.pendingreps != null ? Number(currentEx.pendingreps) : totalReps;
    const isLastRep = pending <= 1;
    const duration = Number(currentEx.duration) || 0;
    const rawTimeForThisRep = capturedTime !== undefined ? Number(capturedTime) : elapsedSec;
    const timeForThisRep = duration > 0
      ? Math.min(Math.max(rawTimeForThisRep || 0, 0), duration)
      : Math.max(rawTimeForThisRep || 0, 0);

    const savedList = exercises.map((ex, i) => {
      if (i !== idx) return ex;
      const accTime = (ex.timeSpentSec || 0) + timeForThisRep;
      if (isLastRep) {
        return {
          ...ex,
          status: 'Completed',
          pendingreps: 0,
          timeSpentSec: accTime,
          repsCompleted: totalReps,
        };
      }
      return {
        ...ex,
        // Rep is complete, but next rep must NOT start until trainer clicks Resume.
        pendingreps: pending - 1,
        timeSpentSec: accTime,
        repsCompleted: (ex.repsCompleted || 0) + 1,
      };
    });

    const nextActiveIdx = isLastRep
      ? savedList.findIndex((e, i) => i > idx && e.status !== 'Completed' && e.status !== 'Skipped' && !e.held)
      : idx;
    const allDone = savedList.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);

    setExercises(savedList);
    if (isLastRep && nextActiveIdx >= 0) setActiveIdx(nextActiveIdx);

    const waitingPatch = {
      elapsedSec: 0,
      isPaused: true,
      isDinging: true,
      waitingForResume: !allDone,
      pauseReason: allDone ? 'workoutComplete' : 'timerComplete',
      // autoStoppedIdx is only for provider-detected pending completions.
      // Once this function records the completed rep/exercise, clear it so
      // navigating back cannot decrement/complete the same unit again.
      autoStoppedIdx: null,
      dingAt: Date.now(),
      ...(allDone ? { finishedAt: Date.now() } : {}),
    };

    sm?.update(patientCard, waitingPatch);
    playDing();

    setTimeout(() => {
      saveExercisesToBE(savedList, 'restarted');
      socketRef?.current?.emit('session:exercise_done', {
        patientCard,
        exercises: savedList,
        activeIdx: nextActiveIdx >= 0 ? nextActiveIdx : idx,
        elapsedSec: 0,
        isPaused: true,
        isDinging: true,
        waitingForResume: !allDone,
        pauseReason: allDone ? 'workoutComplete' : 'timerComplete',
        ...(allDone ? { finishedAt: Date.now() } : {}),
      });
    }, 50);
  };

  const markExerciseSkipped = (idx) => {
    if (!appointment || appointment.status !== 'inprogress') return;
    const exForTime = exercises[idx];
    const skipDur = Number(exForTime?.duration) || 0;
    const timeForThisRep = skipDur > 0 ? Math.min(elapsedSec, skipDur) : elapsedSec; // capture BEFORE reset
    let savedList;
    setExercises((prev) => {
      const updated = [...prev];
      const ex = updated[idx];
      const totalReps    = Number(ex.repetition) || 1;
      const pending      = ex.pendingreps != null ? Number(ex.pendingreps) : totalReps;
      const repsCompleted = totalReps - pending;
      updated[idx] = { ...ex, status: "Skipped",
        timeSpentSec: (ex.timeSpentSec || 0) + timeForThisRep,
        repsCompleted };
      const next = updated.findIndex(
        (e, i) => i > idx && e.status !== "Completed" && e.status !== "Skipped" && !e.held
      );
      if (next >= 0) setActiveIdx(next);
      savedList = updated;
      return updated;
    });
    const allDone = savedList && savedList.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held);
    stopAudio();
    sm?.update(patientCard, {
      elapsedSec: 0,
      isPaused: true,
      isDinging: false,
      waitingForResume: false,
      pauseReason: null,
      autoStoppedIdx: null,
      dingAt: null,
      ...(allDone ? { finishedAt: Date.now() } : {})
    });
    setTimeout(() => {
      if (savedList) saveExercisesToBE(savedList, 'restarted');
      const newActiveIdx = savedList
        ? savedList.findIndex((e, i) => i > idx && e.status !== 'Completed' && e.status !== 'Skipped' && !e.held)
        : activeIdx;
      socketRef?.current?.emit('session:exercise_done', {
        patientCard,
        exercises:  savedList,
        activeIdx:  newActiveIdx >= 0 ? newActiveIdx : activeIdx,
        elapsedSec: 0,
        isPaused:   true,
        isDinging:  false,
        waitingForResume: false,
        pauseReason: null,
        autoStoppedIdx: null,
        dingAt: null,
      });
    }, 50);
  };

  const removeExercise = useCallback((idx) => {
    setExercises((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      setActiveIdx((a) => {
        if (a > idx) return Math.max(0, a - 1);
        if (a >= updated.length) return Math.max(0, updated.length - 1);
        return a;
      });
      return updated;
    });
  }, []);

  const restartExercise = useCallback((idx) => {
    if (!appointment || appointment.status !== 'inprogress') return;
    let savedList;
    setExercises((prev) => {
      const updated = [...prev];
      const ex = updated[idx];
      updated[idx] = {
        ...ex,
        status: 'notStarted',
        pendingreps: Number(ex.repetition) || 1,
        timeSpentSec: undefined,
        repsCompleted: undefined,
      };
      savedList = updated;
      return updated;
    });
    setActiveIdx(idx);
    stopAudio();
    sm?.update(patientCard, { elapsedSec: 0, isPaused: true, isDinging: false, waitingForResume: false, pauseReason: null, autoStoppedIdx: null, dingAt: null });
    setTimeout(() => { if (savedList) saveExercisesToBE(savedList, 'restarted'); }, 50);
  }, [appointment, patientCard, sm, saveExercisesToBE]); // eslint-disable-line

  const saveTimerRef = useRef(null);
  const moveExercise = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setExercises(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, moved);
      // Debounce BE save by 1.5 s to avoid rapid fire during drag
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveExercisesToBE(updated, 'restarted'), 1500);
      return updated;
    });
    setActiveIdx(a => {
      if (a === fromIdx) return toIdx;
      if (fromIdx < a && toIdx >= a) return a - 1;
      if (fromIdx > a && toIdx <= a) return a + 1;
      return a;
    });
  }, [saveExercisesToBE]);

  const updateExercise = useCallback((idx, changes) => {
    let savedList;
    setExercises((prev) => {
      const updated = [...prev];
      const merged = { ...updated[idx], ...changes };
      // When reps change, reset pendingreps to match
      if (changes.repetition != null) {
        merged.pendingreps = Number(changes.repetition);
      }
      updated[idx] = merged;
      savedList = updated;
      return updated;
    });
    // Persist duration/rep changes to BE so other logins see updated plan
    if (changes.duration != null || changes.repetition != null) {
      setTimeout(() => { if (savedList) saveExercisesToBE(savedList, 'restarted'); }, 50);
    }
  }, [saveExercisesToBE]);

  const addCommentToExercise = useCallback((idx, comment) => {
    setExercises(prev => {
      const updated = [...prev];
      const ex = { ...updated[idx] };
      const existing = Array.isArray(ex.commentLog) ? ex.commentLog : [];
      ex.commentLog = [...existing, comment];
      updated[idx] = ex;
      return updated;
    });
  }, []);

  const addExercise = useCallback((exercise) => {
    let savedList;
    setExercises((prev) => {
      let insertAt = prev.length;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].status !== "Completed" && prev[i].status !== "Skipped") {
          insertAt = i + 1;
          break;
        }
      }
      const newEx = {
        ...exercise,
        _localId: (exercise.name || "ex") + "-" + Date.now(),
        status: "notStarted",
        pendingreps: Number(exercise.repetition) || 1,
      };
      const updated = [...prev];
      updated.splice(insertAt, 0, newEx);
      savedList = updated;
      return updated;
    });
    setTimeout(() => { if (savedList) saveExercisesToBE(savedList, 'restarted'); }, 50);
  }, [saveExercisesToBE]);

  const duplicateExercise = useCallback((idx, suffix, duration, repetition) => {
    let savedList;
    setExercises(prev => {
      const src = prev[idx];
      if (!src) return prev;
      const newEx = {
        ...src,
        name: src.name + (suffix ? ' ' + suffix : ''),
        duration: Number(duration) || src.duration,
        repetition: Number(repetition) || src.repetition,
        pendingreps: Number(repetition) || src.repetition,
        status: 'notStarted',
        wasRestarted: false,
        remarks: '',
        commentLog: [],
        timeSpentSec: null,
        repsCompleted: null,
        _localId: (src.name || 'ex') + '-dup-' + Date.now(),
      };
      const updated = [...prev];
      updated.splice(idx + 1, 0, newEx);
      savedList = updated;
      return updated;
    });
    setTimeout(() => { if (savedList) saveExercisesToBE(savedList, 'restarted'); }, 50);
  }, [saveExercisesToBE]);

  const restartSkipped = useCallback((ids, extras = [], editedSkipped = []) => {
    setExercises((prev) => {
      const kept = prev.map(e => {
        if (e.status === 'Skipped' && ids.includes(e._localId)) {
          const edited = (editedSkipped || []).find(ed => ed._localId === e._localId);
          return { ...(edited || e), status: 'notStarted', wasRestarted: true };
        }
        return e;
      });
      const newExtras = (extras || []).map((ex, i) => ({
        ...ex,
        _localId: `extra-${Date.now()}-${i}`,
        status: 'notStarted',
      }));
      return [...kept, ...newExtras];
    });
  }, []);

  /* ── Derived values ───────────────────────────────────────── */
  const completedCount = exercises.filter((e) => e.status === "Completed").length;
  const completedSessions = allSessions.filter((a) => a.status === "Completed").length;
  const sessionNumber = completedSessions + 1;
  const totalPlanned =
    (consultation?.mythsessions || 0) +
    (consultation?.maintsessions || 0) +
    (consultation?.preventivesessions || 0) +
    (consultation?.othersessions1 || 0) +
    (consultation?.othersessions2 || 0) +
    (consultation?.othersessions3 || 0);
  const totalSessions = totalPlanned || allSessions.length || "?";

  const activeExercise = exercises[activeIdx] || {};
  const secsLeft       = exercises
    .slice(activeIdx)
    .filter(e => !e.held)
    .reduce((s, e) => s + (Number(e.duration) || 1) * (Number(e.pendingreps ?? e.repetition) || 1), 0);
  const minutesLeft    = Math.ceil(secsLeft / 60);

  const activeExCount = exercises.filter(e => !e.held).length;
  const efficiency = activeExCount
    ? Math.round((completedCount / activeExCount) * 100)
    : 0;

  const sessionDate = appointment?.start
    ? new Date(appointment.start).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : new Date().toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      });

  const sessionIdShort = appointment?._id
    ? `#${appointment._id.slice(-5).toUpperCase()}`
    : "—";
  const doctorName = appointment?.username || consultation?.therapist || "—";

  /* Map internal statuses to display statuses */
  const getDisplayStatus = (ex, i) => {
    if (ex.held)                   return "held";
    if (ex.status === "Completed") return "done";
    if (ex.status === "Skipped")   return "skipped";
    if (i === activeIdx)           return "active";
    return "todo";
  };
  const displayExercises = exercises.map((ex, i) => ({
    ...ex,
    displayStatus: getDisplayStatus(ex, i),
    title: ex.name || "Exercise",
    time:  ex.duration ? fmtDur(ex.duration) : null,
    delta1: ex.lastMVC  ? `${ex.lastMVC > 0 ? "+" : ""}${ex.lastMVC}%`  : null,
    delta2: ex.lastRom  ? `${ex.lastRom > 0 ? "+" : ""}${ex.lastRom}%`  : null,
    remarkText: ex.remarks || ex.comments || null,
    commentLog: Array.isArray(ex.commentLog) ? ex.commentLog : [],
  }));

  const appliedAdjustments = Object.entries(currentAdjustments).filter(([, v]) => v);

  /* ════════════════════════════════════════════════════════════
     SEARCH SCREEN – shown when no patient selected
  ════════════════════════════════════════════════════════════ */
  if (!patientCard || (!profile && !loading && !error)) {
    return (
      <div className="ipadFrame">
        <div className="screen">
          <div className="topBar glass">
            <div className="patientLine">
              <div className="avatar" />
              <div className="patientText">
                <div className="kLabel" style={{ fontSize: 17, fontWeight: 700 }}>
                  Session View — Search Saadhak
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              marginTop: 60,
              padding: "0 40px",
            }}
          >
            <input
              type="text"
              list="ipad-search-opts"
              value={searchInput}
              placeholder="Enter 3+ characters: Name / Card / Phone"
              onChange={(e) => setSearchInput(e.target.value)}
              onInput={(e) => {
                const val = e.target.value;
                const match = searchResults.find(
                  (p) => `${p.name} (${p.patientCard}) - ${p.number}` === val
                );
                if (match) handlePatientSelect(match);
              }}
              style={{
                width: "100%",
                maxWidth: 480,
                padding: "12px 18px",
                borderRadius: 14,
                border: "1.5px solid rgba(255,255,255,0.28)",
                background: "rgba(255,255,255,0.13)",
                color: "#fff",
                fontSize: 15,
                backdropFilter: "blur(10px)",
                outline: "none",
              }}
            />
            <datalist id="ipad-search-opts">
              {[
                ...new Set(
                  searchResults.map(
                    (p) => `${p.name} (${p.patientCard}) - ${p.number}`
                  )
                ),
              ].map((v, i) => (
                <option key={i} value={v} />
              ))}
            </datalist>
            {isSearching && (
              <div className="kLabel" style={{ opacity: 0.7 }}>
                Searching...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════
     LOADING / ERROR STATES
  ════════════════════════════════════════════════════════════ */
  if (loading) {
    return (
      <div className="ipadFrame">
        <div
          className="screen"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div className="kLabel" style={{ fontSize: 20 }}>
            Loading session data…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ipadFrame">
        <div
          className="screen"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
          }}
        >
          <div className="kLabel" style={{ color: "salmon", fontSize: 15 }}>
            {error}
          </div>
          <button
            className="btn ghost"
            onClick={() => {
              setPatientCard("");
              setProfile(null);
              setError("");
            }}
          >
            Another Saadhak
          </button>
        </div>
      </div>
    );
  }

  /* No appointment today — 1-button auto-create */
  if (profile && !appointment && !loading) {
    const totalSec = consultationAllSeqs.reduce((sum, s) => sum + (Number(s.duration) || 60) * (s.repetition || 1), 0);
    const durationMin = consultationAllSeqs.length > 0 ? Math.max(10, Math.ceil(totalSec / 60)) : 50;
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f0f4f8', gap:24 }}>
        <div style={{ background:'#fff', borderRadius:16, padding:'32px 40px', boxShadow:'0 4px 24px rgba(0,0,0,0.1)', textAlign:'center', maxWidth:420 }}>
          <div style={{ fontSize:28, fontWeight:700, marginBottom:8 }}>{profile.name}</div>
          <div style={{ color:'#666', fontSize:16, marginBottom:4 }}>Card: {patientCard}</div>
          {profile.number && <div style={{ color:'#666', fontSize:16, marginBottom:16 }}>📞 {profile.number}</div>}
          {consultationAllSeqs.length > 0 ? (
            <div style={{ color:'#888', fontSize:14, marginBottom:24 }}>
              Session duration: <strong>{durationMin} min</strong> ({consultationAllSeqs.length} exercises)
            </div>
          ) : (
            <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, padding:'14px 16px', marginBottom:16, fontSize:14, color:'#dc2626', fontWeight:600 }}>
              No exercises assigned. Treatment plan must be approved before starting a session.
            </div>
          )}
          <button
            onClick={createAppointment}
            disabled={creatingAppt || consultationAllSeqs.length === 0}
            style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:12, padding:'16px 48px', fontSize:20, fontWeight:700, cursor: creatingAppt ? 'not-allowed' : 'pointer', opacity: creatingAppt ? 0.7 : 1, width:'100%' }}
          >
            {creatingAppt ? 'Creating…' : '▶ Start Session'}
          </button>
          <button
            onClick={() => { setPatientCard(""); setProfile(null); setError(""); }}
            style={{ marginTop:12, background:'none', border:'none', color:'#888', fontSize:14, cursor:'pointer' }}
          >
            ← Try Another Saadhak
          </button>
        </div>
      </div>
    );
  }

  /* Session already completed — ask to start a fresh one */
  if (profile && appointment?.status === 'Completed' && !loading) {
    const totalSec = consultationAllSeqs.reduce((sum, s) => sum + (Number(s.duration) || 60) * (s.repetition || 1), 0);
    const durationMin = consultationAllSeqs.length > 0 ? Math.max(10, Math.ceil(totalSec / 60)) : 50;
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f0f4f8', gap:24 }}>
        <div style={{ background:'#fff', borderRadius:16, padding:'32px 40px', boxShadow:'0 4px 24px rgba(0,0,0,0.1)', textAlign:'center', maxWidth:440 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:22, fontWeight:800, marginBottom:6, color:'#16a34a' }}>Session Complete!</div>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>{profile.name}</div>
          <div style={{ color:'#888', fontSize:14, marginBottom:20 }}>
            Today's therapy session was already marked as completed.
          </div>
          <button
            onClick={createAppointment}
            disabled={creatingAppt}
            style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:12, padding:'14px 40px', fontSize:17, fontWeight:700, cursor: creatingAppt ? 'not-allowed':'pointer', opacity: creatingAppt ? 0.7:1, width:'100%', marginBottom:12 }}
          >
            {creatingAppt ? 'Creating…' : '▶ Start New Session'}
          </button>
          <div style={{ color:'#94a3b8', fontSize:12, marginBottom:12 }}>
            New appointment: {durationMin} min · {consultationAllSeqs.length} exercises
          </div>
          <button
            onClick={() => { setPatientCard(""); setProfile(null); setError(""); }}
            style={{ background:'none', border:'none', color:'#888', fontSize:14, cursor:'pointer' }}
          >
            ← Another Saadhak
          </button>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════
     MAIN SESSION VIEW
  ════════════════════════════════════════════════════════════ */
  return (
    <div className="ipadFrame">
      {dialogNode}
      <div className="screen">

        {/* ── TOP HEADER ───────────────────────────────────────── */}
        <div className="topBar glass">
          <div className="patientLine">
            <div className="avatar" />
            <div className="patientText">
              <div className="kLabel">
                Saadhak:{" "}
                <span className="kStrong">{profile?.name || patientCard}</span>
              </div>
            </div>
          </div>

          <div className="sessionLine">
            <div className="kLabel" style={{ whiteSpace: 'nowrap' }}>
              Session:{" "}
              <span className="kStrong" style={{ display: 'inline-block', minWidth: 28, textAlign: 'center' }}>{sessionNumber}</span>
              {" / "}
              <span style={{ display: 'inline-block', minWidth: 28, textAlign: 'center' }}>{totalSessions}</span>
              {" "}
              <span
                className={
                  appointment?.status === "inprogress" ? "dotGreen" : "dotOrange"
                }
              />{" "}
              <span className="kStrong">{minutesLeft} min</span> left
            </div>
            {!appointment && (
              <span style={{ fontSize: 13, color: "salmon", opacity: 0.9 }}>No appt today</span>
            )}
          </div>
        </div>

        {/* subBar and effBar removed per request */}

        {/* ── SESSION-OVER WARNING BAR (all exercises done, session not closed) ── */}
        {appointment?.status === 'inprogress' &&
         exercises.length > 0 &&
         exercises.every(e => e.status === 'Completed' || e.status === 'Skipped' || e.held) && (
          <div style={{
            background: 'linear-gradient(90deg,#dc2626,#b91c1c)',
            padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 10,
            borderBottom: '1px solid rgba(0,0,0,0.18)',
          }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, flex: 1 }}>
              All exercises done — please mark session over
            </span>
            <button
              onClick={handleComplete}
              style={{ background: '#fff', color: '#dc2626', border: 'none', borderRadius: 8,
                padding: '5px 14px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
              ✓ Mark Done Now
            </button>
          </div>
        )}

        {/* ── MAIN GRID ────────────────────────────────────────── */}
        <div className="mainGrid">

          {/* LEFT PANEL */}
          <div className="leftPanel glass" style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div className="exerciseHeader">
              <div className="exerciseTitle">
                {activeExercise.name?.toUpperCase() || "NO EXERCISES ASSIGNED"}
              </div>
            </div>

            <div
              className="sectionRow"
              role="button"
              tabIndex={0}
              onClick={() => setShowAdjustments(true)}
              onKeyDown={(e) => e.key === "Enter" && setShowAdjustments(true)}
            >
              <span className="chevBox">▾</span>
              <span className="kStrong">
                Adjustments Applied ({appliedAdjustments.length})
              </span>
            </div>

            {appliedAdjustments.length === 0 ? (
              <div className="bulletBox">
                <div className="bullet" style={{ opacity: 0.4 }}>
                  <span className="dot" /> None applied
                </div>
              </div>
            ) : (
              <div className="adjGrid">
                {appliedAdjustments.map(([label]) => (
                  <div key={label} className="adjItem">
                    <span className="adjDot" /> {label}
                  </div>
                ))}
              </div>
            )}

            {/* ── Consultation updated banner ─── */}
            {consultUpdate && (
              <div style={{
                margin: '6px 8px 0', padding: '8px 12px', borderRadius: 8,
                background: 'linear-gradient(135deg,#fef3c7,#fde68a)',
                border: '1.5px solid #f59e0b', fontSize: 12,
              }}>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 2 }}>
                  ⚠️ Consultation updated
                  {consultUpdate.reviewedBy && <span> by {consultUpdate.reviewedBy}</span>}
                  {" — "}{consultUpdate.date.toLocaleDateString()}
                </div>
                {consultUpdate.seqAdded.length > 0 && (
                  <div style={{ color: '#16a34a' }}>➕ New exercises: {consultUpdate.seqAdded.join(", ")}</div>
                )}
                {consultUpdate.seqRemoved.length > 0 && (
                  <div style={{ color: '#dc2626' }}>➖ Removed: {consultUpdate.seqRemoved.join(", ")}</div>
                )}
                {consultUpdate.recAdded.length > 0 && (
                  <div style={{ color: '#2563eb' }}>➕ New recommendations: {consultUpdate.recAdded.join(", ")}</div>
                )}
                {consultUpdate.type === 'plan_submitted' && !consultUpdate.seqAdded.length && !consultUpdate.recAdded.length && (
                  <div style={{ color: '#7c3aed' }}>Treatment plan submitted. Review exercises and recommendations.</div>
                )}
                <button onClick={() => setConsultUpdate(null)}
                  style={{ marginTop: 4, fontSize: 11, background: 'none', border: 'none',
                    color: '#92400e', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  Dismiss
                </button>
              </div>
            )}

            <div className="sectionRow muted">
              <span className="chevBox">▾</span>
              <span className="kStrong">
                Exercises ({completedCount} / {activeExCount})
              </span>
              <span className="kDots">•••</span>
            </div>

            <div className="list" style={{ overflowY: 'auto', minHeight: 460, maxHeight: 560 }}>
              {displayExercises.length === 0 ? (
                <div className="kLabel" style={{ opacity: 0.5, padding: "12px 8px" }}>
                  No exercises in consultation
                </div>
              ) : (
                displayExercises
                  .map((x, i) => ({ ...x, _realIdx: i }))
                  .filter((x) => x.displayStatus !== "skipped" && x.displayStatus !== "held")
                  .map((x) => {
                    const isNew = consultUpdate?.seqAdded?.includes(x.name);
                    return (
                      <div key={x._localId || x._realIdx}
                        style={isNew ? { outline: '2px solid #16a34a', borderRadius: 8, position: 'relative' } : undefined}>
                        {isNew && (
                          <span style={{ position: 'absolute', top: -8, right: 8, zIndex: 2,
                            fontSize: 10, background: '#16a34a', color: '#fff',
                            borderRadius: 99, padding: '1px 7px', fontWeight: 700 }}>NEW</span>
                        )}
                        <ExerciseRow
                          item={{ ...x, status: x.displayStatus }}
                          isDragging={dragIdxRef.current === x._realIdx && dragOverIdx !== null}
                          isDragOver={dragOverIdx === x._realIdx}
                          onClick={() => setActiveIdx(x._realIdx)}
                          onDone={() => markExerciseDone(x._realIdx)}
                          onSkip={x.displayStatus !== "done" ? () => markExerciseSkipped(x._realIdx) : null}
                          onRemove={x.displayStatus === "todo" ? () => removeExercise(x._realIdx) : null}
                          onEdit={x.displayStatus !== "done" ? () => setEditExIdx(x._realIdx) : null}
                          onDuplicate={x.displayStatus !== "done" ? () => { setDuplicateTargetIdx(x._realIdx); setShowDuplicateModal(true); } : null}
                          onRestart={appointment?.status === 'inprogress' ? () => restartExercise(x._realIdx) : null}
                          onAddComment={comment => addCommentToExercise(x._realIdx, comment)}
                          onDragStart={() => { dragIdxRef.current = x._realIdx; setDragOverIdx(x._realIdx); }}
                          onDragOver={() => setDragOverIdx(x._realIdx)}
                          onDrop={() => { if (dragIdxRef.current != null) moveExercise(dragIdxRef.current, x._realIdx); }}
                          onDragEnd={() => { dragIdxRef.current = null; setDragOverIdx(null); }}
                        />
                      </div>
                    );
                  })
              )}
            </div>

            {/* On Hold exercises section */}
            {exercises.some((e) => e.held) && (
              <>
                <div className="sectionRow muted" style={{ marginTop: 8 }}>
                  <span className="chevBox" style={{ color: "#d97706" }}>🔒</span>
                  <span className="kStrong" style={{ color: "#d97706" }}>
                    On Hold ({exercises.filter((e) => e.held).length}) — not counted in session time
                  </span>
                </div>
                <div className="list" style={{ overflowY: 'auto', maxHeight: 200 }}>
                  {exercises
                    .filter((e) => e.held)
                    .map((x, i) => {
                      const restartDate = x.heldRestartDate;
                      return (
                        <div key={x._localId || i} className="row" style={{ opacity: 0.6, background: '#fffbeb' }}>
                          <div className="rowLeft">
                            <span style={{ color: "#d97706", fontSize: 14 }}>🔒</span>
                          </div>
                          <div className="rowMid">
                            <div className="rowTitle" style={{ color: '#92400e' }}>{x.name || "Exercise"}</div>
                            <div className="rowSub" style={{ color: '#78350f' }}>
                              {x.duration ? fmtDur(x.duration) + '/rep' : ''}
                              {x.heldAt ? ` · held ${new Date(x.heldAt).toLocaleDateString('en-IN')}` : ''}
                              {restartDate ? ` · resumes ${new Date(restartDate).toLocaleDateString('en-IN')}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </>
            )}

            {/* Skipped exercises section */}
            {exercises.some((e) => e.status === "Skipped") && (
              <>
                <div className="sectionRow muted" style={{ marginTop: 8 }}>
                  <span className="chevBox" style={{ color: "#f59e0b" }}>↷</span>
                  <span className="kStrong" style={{ color: "#f59e0b" }}>
                    Skipped ({exercises.filter((e) => e.status === "Skipped").length})
                  </span>
                  <button
                    className="btn ghost"
                    style={{ fontSize: 11, padding: "2px 8px", marginLeft: "auto" }}
                    onClick={() => setShowSkippedRestart(true)}
                  >
                    ↺ Restart
                  </button>
                </div>
                <div className="list" style={{ overflowY: 'auto', maxHeight: 248 }}>
                  {exercises
                    .map((e, i) => ({ ...e, _realIdx: i }))
                    .filter((e) => e.status === "Skipped")
                    .map((x) => (
                      <div
                        key={x._localId || x._realIdx}
                        className="row"
                        style={{ opacity: 0.55 }}
                      >
                        <div className="rowLeft">
                          <span style={{ color: "#f59e0b", fontSize: 14 }}>↷</span>
                        </div>
                        <div className="rowMid">
                          <div className="rowTitle">{x.name || "Exercise"}</div>
                          <div className="rowSub">
                            {x.duration ? fmtDur(x.duration) + '/rep' : ''}
                            {x.repsCompleted != null ? ` · ${x.repsCompleted}/${x.repetition || 1} done` : ''}
                            {x.timeSpentSec != null && x.timeSpentSec > 0 ? ` · ${fmtDur(x.timeSpentSec)} spent` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}

            {/* Add exercise button */}
            {appointment?.status === "inprogress" && (
              <button
                className="btn ghost"
                style={{
                  margin: "8px 4px 4px",
                  fontSize: 12,
                  padding: "6px 12px",
                  width: "calc(100% - 8px)",
                }}
                onClick={() => setShowAddExercise(true)}
              >
                + Add Exercise
              </button>
            )}

            {/* ── rom/mvc moved to bottom section ── */}
            {false && (() => {
              const romParts = sessionParts.filter(p => p.metricType === 'ROM' || !!p.isLinkedFrom);
              if (romParts.length === 0) return <div style={{ padding: 16, color: '#64748b', fontSize: 12 }}>No ROM body parts in this session.</div>;

              const thSt = { padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid rgba(148,163,184,0.15)' };
              const tdSt = { padding: '3px 5px' };
              const numInSt = { width: 64, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 5, padding: '3px 4px', fontSize: 12, color: '#60a5fa', textAlign: 'center', fontWeight: 600, outline: 'none' };
              const numInStP = { ...numInSt, background: 'rgba(147,197,253,0.08)', border: '1px solid rgba(147,197,253,0.2)', color: '#93c5fd' };
              const btnSt = { width: 26, height: 26, borderRadius: 5, background: '#334155', border: '1px solid #475569', color: '#e2e8f0', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

              const hasRomValue = p => {
                const rData = romByPart[p.key];
                return (rData && typeof rData === 'object')
                  ? Object.values(rData).some(mv => mv?.active != null || mv?.passive != null)
                  : false;
              };

              const renderRomCard = p => {
                const clinicalKey = p.romKey || '';
                const side = deriveSide(p.key);
                const romPart = clinicalKey ? clinicalRomParts.find(rp => rp.key === clinicalKey) : null;
                const painV = painByPart[p.key] || 0;
                const linkedLabel = p.isLinkedFrom ? (SIV_PART_LABELS[p.isLinkedFrom] || p.isLinkedFrom) : '';
                return (
                  <div key={p.key} style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 10, padding: '10px 14px', border: p.isLinkedFrom ? '1px solid rgba(96,165,250,0.35)' : '1px solid rgba(148,163,184,0.18)', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9' }}>{p.label}</span>
                      {side && <span style={{ fontSize: 10, color: '#94a3b8' }}>({side === 'left' ? 'Left' : 'Right'})</span>}
                      <span style={{ fontSize: 9, background: '#0ea5e9', color: '#fff', borderRadius: 99, padding: '1px 6px', fontWeight: 700 }}>ROM</span>
                      {p.isLinkedFrom && <span style={{ fontSize: 10, color: '#93c5fd' }}>🔗 {linkedLabel}</span>}
                    </div>
                    {romPart ? (
                      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: 'rgba(15,23,42,0.6)' }}>
                              <th style={{ ...thSt, textAlign: 'left', color: '#94a3b8' }}>Movement</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#475569' }}>Nml°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#60a5fa' }}>Active°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#93c5fd' }}>Passive°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#fca5a5' }}>Pain</th>
                            </tr>
                          </thead>
                          <tbody>
                            {romPart.movements.map((move, mi) => {
                              const mv = romByPart[p.key]?.[move.key] || {};
                              return (
                                <tr key={move.key} style={{ background: mi % 2 ? 'rgba(51,65,85,0.3)' : 'rgba(30,41,59,0.25)' }}>
                                  <td style={{ ...tdSt, color: '#cbd5e1', fontSize: 11 }}>{move.label}</td>
                                  <td style={{ ...tdSt, textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: 11 }}>{move.normal}°</td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.active ?? ''}
                                      onChange={e => { const v = e.target.value === '' ? undefined : Number(e.target.value); setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), active: v } } })); }}
                                      style={numInSt} />
                                  </td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.passive ?? ''}
                                      onChange={e => { const v = e.target.value === '' ? undefined : Number(e.target.value); setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), passive: v } } })); }}
                                      style={numInStP} />
                                  </td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="checkbox" checked={!!mv.pain}
                                      onChange={e => setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), pain: e.target.checked } } }))}
                                      style={{ width: 15, height: 15, accentColor: '#f44336', cursor: 'pointer' }} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#64748b', padding: '4px 0' }}>No movements defined for this part.</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: '#fca5a5', fontWeight: 700 }}>Pain (0-10)</span>
                      <button style={btnSt} onClick={() => setPainByPart(p2 => ({ ...p2, [p.key]: Math.max(0, painV - 1) }))}>−</button>
                      <span style={{ fontSize: 16, fontWeight: 800, color: '#f87171', minWidth: 22, textAlign: 'center' }}>{painV}</span>
                      <button style={btnSt} onClick={() => setPainByPart(p2 => ({ ...p2, [p.key]: Math.min(10, painV + 1) }))}>+</button>
                    </div>
                  </div>
                );
              };

              const zeroParts = romParts.filter(p => !hasRomValue(p));
              const nonZeroParts = romParts.filter(p => hasRomValue(p));

              return (
                <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>ROM Readings</div>
                  {zeroParts.map(renderRomCard)}
                  {nonZeroParts.length > 0 && (
                    <>
                      <div onClick={() => setRomNonZeroOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'rgba(14,165,233,0.12)', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(14,165,233,0.25)', marginBottom: romNonZeroOpen ? 8 : 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#38bdf8', letterSpacing: '0.03em', flex: 1 }}>
                          Already Recorded ({nonZeroParts.length})
                        </span>
                        <span style={{ fontSize: 13, color: '#38bdf8' }}>{romNonZeroOpen ? '▴' : '▾'}</span>
                      </div>
                      {romNonZeroOpen && nonZeroParts.map(renderRomCard)}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── MVC tab (moved to bottom section) ── */}
            {false && (() => {
              const mvcParts = sessionParts.filter(p => p.metricType === 'MVC');
              if (mvcParts.length === 0) return <div style={{ padding: 16, color: '#64748b', fontSize: 12 }}>No MVC body parts in this session.</div>;

              const thSt = { padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid rgba(148,163,184,0.15)' };
              const tdSt = { padding: '3px 5px' };
              const btnSt = { width: 26, height: 26, borderRadius: 5, background: '#334155', border: '1px solid #475569', color: '#e2e8f0', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

              const hasMvcValue = p => {
                const mData = mvcByPart[p.key];
                return (mData && typeof mData === 'object')
                  ? Object.values(mData).some(mv => mv?.grade != null && mv?.grade !== '')
                  : false;
              };

              const renderMvcCard = p => {
                const side = deriveSide(p.key);
                const painV = painByPart[p.key] || 0;
                return (
                  <div key={p.key} style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(16,185,129,0.25)', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9' }}>{p.label}</span>
                      {side && <span style={{ fontSize: 10, color: '#94a3b8' }}>({side === 'left' ? 'Left' : 'Right'})</span>}
                      <span style={{ fontSize: 9, background: '#10b981', color: '#fff', borderRadius: 99, padding: '1px 6px', fontWeight: 700 }}>MVC</span>
                    </div>
                    <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: 'rgba(15,23,42,0.6)' }}>
                            <th style={{ ...thSt, textAlign: 'left', color: '#94a3b8' }}>Muscle Group</th>
                            <th style={{ ...thSt, textAlign: 'center', color: '#34d399' }}>Grade (MVC 0-5)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[{ key: 'overall', label: 'MVC Grade (0-5)' }].map((grp, gi) => {
                            const gradeVal = mvcByPart[p.key]?.[grp.key]?.grade ?? '';
                            return (
                              <tr key={grp.key} style={{ background: gi % 2 ? 'rgba(51,65,85,0.3)' : 'rgba(30,41,59,0.25)' }}>
                                <td style={{ ...tdSt, color: '#cbd5e1', fontSize: 11 }}>{grp.label}</td>
                                <td style={{ ...tdSt, textAlign: 'center' }}>
                                  <select value={gradeVal}
                                    onChange={e => setMvcByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [grp.key]: { grade: e.target.value } } }))}
                                    style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 5, padding: '3px 8px', fontSize: 12, color: '#34d399', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                                    <option value="">—</option>
                                    <option value="0">0 – No contraction</option>
                                    <option value="1">1 – Trace / flicker</option>
                                    <option value="2">2 – Gravity eliminated</option>
                                    <option value="3">3 – Against gravity</option>
                                    <option value="4">4 – Some resistance</option>
                                    <option value="5">5 – Normal</option>
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: '#fca5a5', fontWeight: 700 }}>Pain (0-10)</span>
                      <button style={btnSt} onClick={() => setPainByPart(p2 => ({ ...p2, [p.key]: Math.max(0, painV - 1) }))}>−</button>
                      <span style={{ fontSize: 16, fontWeight: 800, color: '#f87171', minWidth: 22, textAlign: 'center' }}>{painV}</span>
                      <button style={btnSt} onClick={() => setPainByPart(p2 => ({ ...p2, [p.key]: Math.min(10, painV + 1) }))}>+</button>
                    </div>
                  </div>
                );
              };

              const zeroParts = mvcParts.filter(p => !hasMvcValue(p));
              const nonZeroParts = mvcParts.filter(p => hasMvcValue(p));

              return (
                <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>MVC Readings</div>
                  {zeroParts.map(renderMvcCard)}
                  {nonZeroParts.length > 0 && (
                    <>
                      <div onClick={() => setMvcNonZeroOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'rgba(16,185,129,0.1)', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(16,185,129,0.25)', marginBottom: mvcNonZeroOpen ? 8 : 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#34d399', letterSpacing: '0.03em', flex: 1 }}>
                          Already Recorded ({nonZeroParts.length})
                        </span>
                        <span style={{ fontSize: 13, color: '#34d399' }}>{mvcNonZeroOpen ? '▴' : '▾'}</span>
                      </div>
                      {mvcNonZeroOpen && nonZeroParts.map(renderMvcCard)}
                    </>
                  )}
                </div>
              );
            })()}

          </div>

          {/* RIGHT PANEL */}
          <div className="rightPanel" style={{ overflowY: 'auto', paddingBottom: 64 }}>
            <div className="timerCard glass">
              <div className="timerInner">
                <div
                  className="ring"
                  aria-hidden="true"
                  onClick={handleCircleClick}
                  style={{ cursor: "pointer" }}
                  title={isDinging ? "Stop buzzer" : isWaitingForResume ? "Waiting - click Resume button" : isPaused ? "Resume" : "Pause"}
                >
                  <div className="ringCut" />
                  <div className="ringCenter">
                    <div className="timerBig">{formatTime(Math.max(0, (Number(activeExercise.duration) || 0) - elapsedSec))}</div>
                    {(() => {
                      const total   = Number(activeExercise.repetition) || 1;
                      const pending = activeExercise.pendingreps != null ? Number(activeExercise.pendingreps) : total;
                      const current = total - pending + 1;
                      const dur     = Number(activeExercise.duration) || 1;
                      const innerPct = Math.min(100, Math.round((elapsedSec / dur) * 100));
                      const color = SEGMENT_COLORS[activeIdx % SEGMENT_COLORS.length];
                      return (
                        <DualRings
                          outerRadius={88}
                          outerStroke={10}
                          innerRadius={68}
                          innerStroke={8}
                          innerProgress={innerPct}
                          innerColor={color}
                          time={`${current}/${total}`}
                          fontSize={22}
                          outerSegments={segments}
                        />
                      );
                    })()}
                  </div>
                </div>

                {/* ── Exercise name label below ring ── */}
                <div className="exerciseNameLabel">
                  {activeExercise.name || "—"}
                </div>

                {/* ── Embedded video or leg figure ── */}
                {showVideoPlayer && appointment && !videoPopped ? (
                  <div style={{
                    position: 'absolute', right: 8, bottom: 8,
                    width: 200, height: 152, zIndex: 2,
                  }}>
                    <SessionVideoPlayer
                      ref={videoPlayerRef}
                      patientCard={patientCard}
                      embedded
                      onClose={() => setShowVideoPlayer(false)}
                      onPopOut={() => setVideoPopped(true)}
                    />
                  </div>
                ) : (
                  <div className="figure" aria-hidden="true">
                    
                  </div>
                )}
              </div>

              <div className="timerButtons">
                {appointment?.status === "scheduled" ? (
                  <button className="btn primary" style={{ height: 40, fontSize: 15, padding: "0 28px", fontWeight: 800 }} onClick={handleStartSession}>
                    ▶ Start Session
                  </button>
                ) : (
                  <>
                    <button
                      className="btn ghost"
                      onClick={handlePause}
                      title={isDinging ? "Stop buzzer first. Timer will stay waiting until Resume is clicked again." : undefined}
                    >
                      <span className="iconPause" />{" "}
                      {isDinging ? "Stop Buzzer" : isWaitingForResume ? "Resume" : isPaused ? "Resume" : "Pause"}
                    </button>
                    <button
                      className="btn ghost"
                      onClick={() => markExerciseSkipped(activeIdx)}
                      title="Skip the current exercise. If a rep just completed, acknowledge the buzzer first."
                      disabled={
                        appointment?.status !== 'inprogress' ||
                        isDinging ||
                        exercises[activeIdx]?.status === 'Completed' ||
                        exercises[activeIdx]?.status === 'Skipped' ||
                        exercises[activeIdx]?.held
                      }
                    >
                      <span className="iconCheck" /> Skip
                    </button>
                    <button
                      className="btn ghost square"
                      aria-label="Adjustments"
                      onClick={() => setShowAdjustments(true)}
                    >
                      ▤
                    </button>
                  </>
                )}
              </div>

              {/* ── Skip All Remaining ── */}
              {appointment?.status === 'inprogress' &&
               exercises.some(e => e.status !== 'Completed' && e.status !== 'Skipped') && (
                <button
                  style={{ display: 'block', margin: '4px auto 0', fontSize: 11, padding: '3px 12px', background: 'rgba(180,83,9,.08)', border: '1px solid rgba(245,158,11,.30)', borderRadius: 7, color: '#b45309', cursor: 'pointer', fontWeight: 700 }}
                  title="Skip all remaining exercises"
                  onClick={() => {
                    const updated = exercises.map(e =>
                      e.status !== 'Completed' && e.status !== 'Skipped'
                        ? { ...e, status: 'Skipped' } : e
                    );
                    setExercises(updated);
                    stopAudio();
                    const patch = {
                      exercises: updated,
                      elapsedSec: 0,
                      isPaused: true,
                      isDinging: false,
                      waitingForResume: false,
                      pauseReason: 'workoutComplete',
                      autoStoppedIdx: null,
                      dingAt: null,
                      finishedAt: Date.now(),
                    };
                    sm?.update(patientCard, patch);
                    emitFullState(patch);
                    saveExercisesToBE(updated, 'restarted');
                  }}
                >
                  ↷↷ Skip All Remaining
                </button>
              )}


            </div>

            {/* ── RECOMMENDATIONS (right panel, collapsed by default) ─── */}
            <div className="recommPanel glass">
              <div className="recommHeader" onClick={() => setShowRecommendations(r => !r)}>
                <span className="chevBox">{showRecommendations ? '▴' : '▾'}</span>
                <span className="kStrong" style={{ fontSize: 14 }}>
                  Daily Recommendations ({sessionRecomms.length})
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'rgba(20,20,22,.45)' }}>
                  {showRecommendations ? 'Collapse' : 'Expand to update for today'}
                </span>
              </div>
              {showRecommendations && (
                <div className="recommBody">
                  {sessionRecomms.length === 0 ? (
                    <div style={{ padding: '12px 14px', fontSize: 13, opacity: 0.45 }}>No recommendations on file</div>
                  ) : sessionRecomms.map((r, i) => {
                    const recName = r.name || r.title || String(r);
                    const isNewRec = consultUpdate?.recAdded?.includes(recName);
                    return (
                    <div key={i} className="recommRow"
                      style={isNewRec ? { background: '#dcfce7', borderLeft: '3px solid #16a34a' } : undefined}>
                      <div className="recommName" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {isNewRec && <span style={{ fontSize: 10, background: '#16a34a', color: '#fff',
                          borderRadius: 99, padding: '1px 6px', fontWeight: 700, flexShrink: 0 }}>NEW</span>}
                        {recName}
                      </div>
                      <div className="recommControls">
                        <button className="recommBtn"
                          style={{ background: r.completed === true ? '#16a34a' : 'rgba(0,0,0,.06)', color: r.completed === true ? '#fff' : 'rgba(20,20,22,.55)' }}
                          onClick={() => setSessionRecomms(prev => prev.map((x, j) => j === i ? { ...x, completed: true } : x))}>
                          ✓ Done
                        </button>
                        <button className="recommBtn"
                          style={{ background: r.completed === false ? '#dc2626' : 'rgba(0,0,0,.06)', color: r.completed === false ? '#fff' : 'rgba(20,20,22,.55)' }}
                          onClick={() => setSessionRecomms(prev => prev.map((x, j) => j === i ? { ...x, completed: false } : x))}>
                          ✗ Skipped
                        </button>
                        <input
                          type="number" min={0}
                          className="recommInput"
                          placeholder="Qty / Amount"
                          value={r.qty || ''}
                          onChange={e => setSessionRecomms(prev => prev.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))}
                        />
                        {r.unit && <span style={{ fontSize: 12, color: 'rgba(20,20,22,.50)', marginLeft: 2 }}>{r.unit}</span>}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            {/* ── Per-body-part readings moved to bottom light section ── */}
            {sessionParts.length > 0 ? null : (() => {
              const btnSt = { width: 26, height: 26, borderRadius: 5, background: '#334155', border: '1px solid #475569', color: '#e2e8f0', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
              const numInSt = { width: 64, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 5, padding: '3px 4px', fontSize: 12, color: '#60a5fa', textAlign: 'center', fontWeight: 600, outline: 'none' };
              const numInStP = { ...numInSt, background: 'rgba(147,197,253,0.08)', border: '1px solid rgba(147,197,253,0.2)', color: '#93c5fd' };
              const thSt = { padding: '4px 6px', fontWeight: 600, borderBottom: '1px solid rgba(148,163,184,0.15)' };
              const tdSt = { padding: '3px 5px' };

              const renderPartCard = ({ key, label, metricType, isLinkedFrom, romKey: pRomKey, muscles: pMuscles }) => {
                const painV = painByPart[key] || 0;
                const mt = metricType || '';
                const showRom = mt === 'ROM' || mt === 'both' || !!isLinkedFrom;
                const showMvc = mt === 'MVC' || mt === 'both';
                const linkedLabel = isLinkedFrom ? (SIV_PART_LABELS[isLinkedFrom] || isLinkedFrom) : '';
                const clinicalKey = pRomKey || '';
                const side = deriveSide(key);
                const romPart = clinicalKey ? clinicalRomParts.find(p => p.key === clinicalKey) : null;

                return (
                  <div key={key} style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 10, padding: '10px 14px', border: isLinkedFrom ? '1px solid rgba(96,165,250,0.35)' : '1px solid rgba(148,163,184,0.18)', marginBottom: 8 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}>{label}</span>
                      {side && <span style={{ fontSize: 10, color: '#94a3b8' }}>({side === 'left' ? 'Left' : 'Right'})</span>}
                      {mt && <span style={{ fontSize: 9, background: mt === 'both' ? '#f59e0b' : showRom ? '#0ea5e9' : '#10b981', color: '#fff', borderRadius: 99, padding: '1px 6px', fontWeight: 700 }}>{mt === 'both' ? 'ROM+MVC' : mt}</span>}
                      {isLinkedFrom && <span style={{ fontSize: 10, color: '#93c5fd' }}>🔗 {linkedLabel}</span>}
                    </div>

                    {/* ROM movements table */}
                    {showRom && romPart && (
                      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'rgba(15,23,42,0.6)' }}>
                              <th style={{ ...thSt, textAlign: 'left', color: '#94a3b8' }}>Movement</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#475569' }}>Nml°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#60a5fa' }}>Active°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#93c5fd' }}>Passive°</th>
                              <th style={{ ...thSt, textAlign: 'center', color: '#fca5a5' }}>Pain</th>
                            </tr>
                          </thead>
                          <tbody>
                            {romPart.movements.map((move, mi) => {
                              const mv = romByPart[key]?.[move.key] || {};
                              return (
                                <tr key={move.key} style={{ background: mi % 2 ? 'rgba(51,65,85,0.3)' : 'rgba(30,41,59,0.25)' }}>
                                  <td style={{ ...tdSt, color: '#cbd5e1', fontSize: 11 }}>{move.label}</td>
                                  <td style={{ ...tdSt, textAlign: 'center', color: '#475569', fontWeight: 600, fontSize: 11 }}>{move.normal}°</td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.active ?? ''}
                                      onChange={e => {
                                        const v = e.target.value === '' ? undefined : Number(e.target.value);
                                        setRomByPart(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [move.key]: { ...(prev[key]?.[move.key] || {}), active: v } } }));
                                      }}
                                      style={numInSt}
                                    />
                                  </td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.passive ?? ''}
                                      onChange={e => {
                                        const v = e.target.value === '' ? undefined : Number(e.target.value);
                                        setRomByPart(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [move.key]: { ...(prev[key]?.[move.key] || {}), passive: v } } }));
                                      }}
                                      style={numInStP}
                                    />
                                  </td>
                                  <td style={{ ...tdSt, textAlign: 'center' }}>
                                    <input type="checkbox" checked={!!mv.pain}
                                      onChange={e => setRomByPart(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [move.key]: { ...(prev[key]?.[move.key] || {}), pain: e.target.checked } } }))}
                                      style={{ width: 15, height: 15, accentColor: '#f44336', cursor: 'pointer' }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* MVC muscle tables */}
                    {showMvc && (() => {
                      const muscleKeys = pMuscles || [];
                      const mLookup = buildMuscleLookup(clinicalMuscleParts);
                      const muscles = muscleKeys.map(mk => mLookup[mk]).filter(Boolean);
                      if (muscles.length === 0) return (
                        <div style={{ color: '#6b7280', fontSize: 11, padding: '6px 0', fontStyle: 'italic' }}>No muscles mapped for this body part</div>
                      );
                      const mvcSelSt = { background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 4, padding: '2px 4px', fontSize: 11, color: '#34d399', fontWeight: 600, cursor: 'pointer', outline: 'none' };
                      const mvcNumSt = { width: 68, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 4, padding: '2px 4px', fontSize: 11, color: '#34d399', textAlign: 'center', fontWeight: 600, outline: 'none' };
                      const mvcTxtSt = { width: 60, background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', borderRadius: 4, padding: '2px 4px', fontSize: 10, color: '#a7f3d0', outline: 'none' };
                      const setMvcField = (muscleKey, groupKey, field, val) => {
                        setMvcByPart(prev => ({
                          ...prev,
                          [key]: {
                            ...(prev[key] || {}),
                            [muscleKey]: {
                              ...((prev[key] || {})[muscleKey] || {}),
                              [groupKey]: {
                                ...(((prev[key] || {})[muscleKey] || {})[groupKey] || {}),
                                [field]: val,
                              },
                            },
                          },
                        }));
                      };
                      return (
                        <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                          {muscles.map(muscle => (
                            <div key={muscle.key} style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#a7f3d0', padding: '4px 0 2px', borderBottom: '1px solid rgba(52,211,153,0.2)' }}>
                                {muscle.icon} {muscle.label}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                  <tr style={{ background: 'rgba(15,23,42,0.5)' }}>
                                    <th style={{ ...thSt, textAlign: 'left', color: '#94a3b8', fontSize: 10 }}>Subgroup</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#34d399', fontSize: 10 }}>Grade</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#34d399', fontSize: 10 }}>Str(N)</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#34d399', fontSize: 10 }}>Contract</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#34d399', fontSize: 10 }}>Endur(s)</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#34d399', fontSize: 10 }}>Fatigue</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#fca5a5', fontSize: 10 }}>Pain</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#fbbf24', fontSize: 10 }}>Spasm</th>
                                    <th style={{ ...thSt, textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {muscle.groups.map((grp, gi) => {
                                    const gd = mvcByPart[key]?.[muscle.key]?.[grp.key] || {};
                                    return (
                                      <tr key={grp.key} style={{ background: gi % 2 ? 'rgba(51,65,85,0.3)' : 'rgba(30,41,59,0.25)' }}>
                                        <td style={{ ...tdSt, color: '#cbd5e1', fontSize: 10, whiteSpace: 'nowrap' }}>{grp.label}</td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <select value={gd.grade ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'grade', e.target.value)} style={mvcSelSt}>
                                            <option value="">—</option>
                                            <option value="0">0</option><option value="1">1</option><option value="2">2</option>
                                            <option value="3">3</option><option value="4">4</option><option value="5">5</option>
                                          </select>
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <input type="number" inputMode="decimal" min="0" max="999.9999" step="any" value={gd.strength ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'strength', e.target.value === '' ? undefined : Number(e.target.value))} style={mvcNumSt} />
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <select value={gd.contraction ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'contraction', e.target.value)} style={{ ...mvcSelSt, fontSize: 10 }}>
                                            <option value="">—</option>
                                            <option value="Normal">Normal</option><option value="Weak">Weak</option>
                                            <option value="Tremor">Tremor</option><option value="None">None</option>
                                          </select>
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <input type="number" min="0" max="999" value={gd.endurance ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'endurance', e.target.value === '' ? undefined : Number(e.target.value))} style={mvcNumSt} />
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <select value={gd.fatigue ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'fatigue', e.target.value)} style={{ ...mvcSelSt, fontSize: 10, width: 38 }}>
                                            <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                          </select>
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <input type="number" min="0" max="10" value={gd.pain ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'pain', e.target.value === '' ? undefined : Number(e.target.value))} style={{ ...mvcNumSt, width: 36, color: '#fca5a5' }} />
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <select value={gd.spasm ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'spasm', e.target.value)} style={{ ...mvcSelSt, fontSize: 10, width: 38, color: '#fbbf24' }}>
                                            <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                          </select>
                                        </td>
                                        <td style={{ ...tdSt, textAlign: 'center' }}>
                                          <input type="text" value={gd.notes ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'notes', e.target.value)} style={mvcTxtSt} placeholder="..." />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Overall Pain */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: '#fca5a5', fontWeight: 700 }}>Pain (0-10)</span>
                      <button style={btnSt} onClick={() => setPainByPart(p => ({ ...p, [key]: Math.max(0, painV - 1) }))}>−</button>
                      <span style={{ fontSize: 16, fontWeight: 800, color: '#f87171', minWidth: 22, textAlign: 'center' }}>{painV}</span>
                      <button style={btnSt} onClick={() => setPainByPart(p => ({ ...p, [key]: Math.min(10, painV + 1) }))}>+</button>
                    </div>
                  </div>
                );
              };

              // Split: non-zero = any metric has a value
              const hasValue = p => {
                const rData = romByPart[p.key];
                const mData = mvcByPart[p.key];
                const hasRom = rData && typeof rData === 'object'
                  ? Object.values(rData).some(mv => mv?.active != null || mv?.passive != null)
                  : (rData || 0) > 0;
                const hasMvc = mData && typeof mData === 'object'
                  ? Object.values(mData).some(mv => mv?.grade != null && mv?.grade !== '')
                  : (mData || 0) > 0;
                return hasRom || hasMvc || (painByPart[p.key] || 0) > 0;
              };
              const nonZeroParts = sessionParts.filter(hasValue);
              const zeroParts    = sessionParts.filter(p => !hasValue(p));

              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    Body Part Readings
                  </div>

                  {/* ── Non-zero: always expanded ── */}
                  {nonZeroParts.map(renderPartCard)}

                  {/* ── Zero: collapsible ── */}
                  {zeroParts.length > 0 && (
                    <>
                      <div
                        onClick={() => setZeroPartsOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'rgba(51,65,85,0.35)', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(148,163,184,0.12)', marginBottom: zeroPartsOpen ? 8 : 0 }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b', letterSpacing: '0.03em', flex: 1 }}>
                          Not yet recorded ({zeroParts.length})
                        </span>
                        <span style={{ fontSize: 13, color: '#64748b' }}>{zeroPartsOpen ? '▴' : '▾'}</span>
                      </div>
                      {zeroPartsOpen && zeroParts.map(renderPartCard)}
                    </>
                  )}
                </div>
              );
            }) ? null : null}
            {sessionParts.length === 0 && (
              <>
                <MetricCard
                  title="MVC (000.0000)"
                  value={Number(mvc).toFixed(4)}
                  suffix="+"
                  note={`History: +${mvc > 0 ? 1 : 0} from last session`}
                  bars={5}
                  activeBars={Math.min(5, Math.round(mvc))}
                  chartType="area"
                  onDecrement={() => setMvc((v) => Math.max(0, +(v - 0.0001).toFixed(4)))}
                  onIncrement={() => setMvc((v) => +(v + 0.0001).toFixed(4))}
                />
                <MetricCard
                  title="RoM (°)"
                  value={`${Number(rom).toFixed(2)}°`}
                  note={`Goal: 25° | ${Math.min(100, Math.round((rom / 25) * 100))}% achieved`}
                  bars={5}
                  activeBars={Math.min(5, Math.round(rom / 20))}
                  chartType="area"
                  onDecrement={() => setRom((v) => Math.max(0, +(v - 0.01).toFixed(2)))}
                  onIncrement={() => setRom((v) => +(v + 0.01).toFixed(2))}
                />
                <MetricCard
                  title="Pain (0 – 10)"
                  value={pain}
                  suffix="+"
                  note={`Session ID ${sessionIdShort} • D: ${doctorName} • ${sessionDate}`}
                  bars={5}
                  activeBars={Math.min(5, Math.ceil(pain / 2))}
                  chartType="bars"
                  onDecrement={() => setPain((v) => Math.max(0, v - 1))}
                  onIncrement={() => setPain((v) => Math.min(10, v + 1))}
                />
              </>
            )}


            {/* ── SESSION HISTORY ── */}
            {(() => {
              const completedHistory = allSessions
                .filter(s => s.status === 'Completed')
                .sort((a, b) => new Date(b.start) - new Date(a.start));
              if (completedHistory.length === 0) return null;
              return (
                <div style={{ marginTop: 10 }}>
                  <div
                    onClick={() => setShowHistoryPanel(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'rgba(51,65,85,0.45)', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(148,163,184,0.12)', marginBottom: showHistoryPanel ? 6 : 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.03em', flex: 1 }}>
                      History ({completedHistory.length})
                    </span>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{showHistoryPanel ? '▴' : '▾'}</span>
                  </div>
                  {showHistoryPanel && completedHistory.map((s, i) => {
                    const snap = s.sessionFeedback?.exerciseSnapshot || [];
                    const doneCount = snap.filter(e => e.status === 'Completed').length;
                    const dateStr = new Date(s.start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
                    const therapist = s.leadTherapist || s.username || '—';
                    const totalExs = snap.length || '?';
                    const sessionIdx = completedHistory.length - i; // reverse index (oldest = session 1)
                    return (
                      <div key={s._id}
                        onClick={() => setHistorySession(s)}
                        style={{ background: 'rgba(30,42,58,0.85)', borderRadius: 8, padding: '8px 10px', marginBottom: 5, cursor: 'pointer', border: '1px solid rgba(148,163,184,0.10)', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(96,165,250,0.5)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(148,163,184,0.10)'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#60a5fa', background: 'rgba(96,165,250,0.12)', borderRadius: 5, padding: '1px 6px', flexShrink: 0 }}>
                            #{sessionIdx}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', flex: 1 }}>{dateStr}</span>
                          <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 700 }}>{doneCount}/{totalExs} done</span>
                          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>›</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>
                          {therapist}{s.sessionComments ? ` · ${s.sessionComments.slice(0, 40)}${s.sessionComments.length > 40 ? '…' : ''}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── ROM / MVC READINGS (LabReadings-style, light theme) ── */}
        {sessionParts.length > 0 && (() => {
          const romParts = sessionParts.filter(p => p.metricType === 'ROM' || p.metricType === 'both' || !!p.isLinkedFrom);
          const mvcParts = sessionParts.filter(p => p.metricType === 'MVC' || p.metricType === 'both');

          // ── Shared style tokens (light theme) ──
          const TH = { padding: '7px 10px', fontSize: 11, fontWeight: 600, borderBottom: '2px solid #e5e7eb', background: '#f8fafc', whiteSpace: 'nowrap' };
          const TD = { padding: '6px 10px', fontSize: 12, borderBottom: '1px solid #f0f0f0' };
          const inRom  = { width: 64, padding: '3px 5px', fontSize: 12, fontWeight: 700, textAlign: 'center', borderRadius: 5, border: '1px solid #90caf9', background: '#e3f2fd44', color: '#1565c0', outline: 'none' };
          const inPass = { ...inRom, border: '1px solid #bbdefb', background: '#f8fbff', color: '#1976d2' };

          const hasRomValue = p => {
            const d = romByPart[p.key];
            return d && typeof d === 'object' && Object.values(d).some(mv => mv?.active != null || mv?.passive != null);
          };
          const hasMvcValue = p => {
            const d = mvcByPart[p.key];
            if (!d || typeof d !== 'object') return false;
            // New structure: { muscleKey: { groupKey: { grade, ... } } }
            return Object.values(d).some(muscle =>
              muscle && typeof muscle === 'object' && Object.values(muscle).some(grp =>
                grp && typeof grp === 'object' && (grp.grade != null && grp.grade !== '')
              )
            );
          };

          const renderRomCard = (p, compact = false) => {
            const clinicalKey = p.romKey || '';
            const side = deriveSide(p.key);
            const romPart = clinicalKey ? clinicalRomParts.find(r => r.key === clinicalKey) : null;
            const linkedLabel = p.isLinkedFrom ? (SIV_PART_LABELS[p.isLinkedFrom] || p.isLinkedFrom) : '';
            const painV = painByPart[p.key] || 0;
            // In compact mode (Already Recorded), only show movements with actual data
            const movements = romPart ? (compact
              ? romPart.movements.filter(move => {
                  const mv = romByPart[p.key]?.[move.key] || {};
                  return mv.active !== undefined || mv.passive !== undefined || mv.pain;
                })
              : romPart.movements) : [];
            return (
              <div key={p.key} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 2px 10px rgba(0,0,0,.06)', marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{p.label}</span>
                  {side && <span style={{ fontSize: 10, color: '#64748b' }}>({side === 'left' ? 'Left' : 'Right'})</span>}
                  <span style={{ fontSize: 9, background: '#dbeafe', color: '#1565c0', borderRadius: 99, padding: '1px 7px', fontWeight: 700 }}>ROM</span>
                  {p.isLinkedFrom && <span style={{ fontSize: 10, color: '#3b82f6' }}>🔗 {linkedLabel}</span>}
                </div>
                {romPart && movements.length > 0 ? (
                  <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ ...TH, textAlign: 'left', color: '#374151' }}>Movement</th>
                          <th style={{ ...TH, textAlign: 'center', color: '#9ca3af' }}>Nml°</th>
                          <th style={{ ...TH, textAlign: 'center', color: '#1565c0' }}>Active°</th>
                          <th style={{ ...TH, textAlign: 'center', color: '#1976d2' }}>Passive°</th>
                          <th style={{ ...TH, textAlign: 'center', color: '#dc2626' }}>Pain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((move, mi) => {
                          const mv = romByPart[p.key]?.[move.key] || {};
                          return (
                            <tr key={move.key} style={{ background: mi % 2 ? '#fafbfd' : '#fff' }}>
                              <td style={{ ...TD, color: '#374151' }}>{move.label}</td>
                              <td style={{ ...TD, textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{move.normal}°</td>
                              <td style={{ ...TD, textAlign: 'center' }}>
                                <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.active ?? ''}
                                  onChange={e => { const v = e.target.value === '' ? undefined : Number(e.target.value); setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), active: v } } })); }}
                                  style={inRom} />
                              </td>
                              <td style={{ ...TD, textAlign: 'center' }}>
                                <input type="number" inputMode="decimal" min="0" max="360" step="any" value={mv.passive ?? ''}
                                  onChange={e => { const v = e.target.value === '' ? undefined : Number(e.target.value); setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), passive: v } } })); }}
                                  style={inPass} />
                              </td>
                              <td style={{ ...TD, textAlign: 'center' }}>
                                <input type="checkbox" checked={!!mv.pain}
                                  onChange={e => setRomByPart(prev => ({ ...prev, [p.key]: { ...(prev[p.key] || {}), [move.key]: { ...(prev[p.key]?.[move.key] || {}), pain: e.target.checked } } }))}
                                  style={{ width: 15, height: 15, accentColor: '#dc2626', cursor: 'pointer' }} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <div style={{ fontSize: 11, color: '#9ca3af', padding: '4px 0' }}>{romPart ? 'No data recorded.' : 'No movements defined.'}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>Pain (0-10)</span>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setPainByPart(prev => ({ ...prev, [p.key]: Math.max(0, painV - 1) }))}
                    style={{ width: 24, height: 24, borderRadius: 5, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', fontSize: 15, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>−</button>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#dc2626', minWidth: 20, textAlign: 'center', flexShrink: 0 }}>{painV}</span>
                  <button onClick={() => setPainByPart(prev => ({ ...prev, [p.key]: Math.min(10, painV + 1) }))}
                    style={{ width: 24, height: 24, borderRadius: 5, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', fontSize: 15, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>+</button>
                </div>
              </div>
            );
          };

          const renderMvcCard = (p, compact = false) => {
            const side = deriveSide(p.key);
            const painV = painByPart[p.key] || 0;
            const muscleKeys = p.muscles || [];
            const mLookup = buildMuscleLookup(clinicalMuscleParts);
            const allMuscles = muscleKeys.map(mk => mLookup[mk]).filter(Boolean);
            // In compact mode, filter out muscles/groups with no data
            const muscles = compact ? allMuscles.map(muscle => {
              const filteredGroups = muscle.groups.filter(grp => {
                const gd = mvcByPart[p.key]?.[muscle.key]?.[grp.key] || {};
                return gd.grade || gd.strength !== undefined || gd.contraction ||
                       gd.endurance !== undefined || (gd.pain !== undefined && gd.pain !== '') ||
                       gd.fatigue || gd.spasm || gd.notes;
              });
              return filteredGroups.length > 0 ? { ...muscle, groups: filteredGroups } : null;
            }).filter(Boolean) : allMuscles;
            const mvcSelLt = { background: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: 4, padding: '2px 4px', fontSize: 11, color: '#7b1fa2', fontWeight: 600, cursor: 'pointer', outline: 'none' };
            const mvcNumLt = { width: 68, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 4, padding: '2px 4px', fontSize: 11, color: '#7b1fa2', textAlign: 'center', fontWeight: 600, outline: 'none' };
            const mvcTxtLt = { width: 60, background: '#fefcff', border: '1px solid #f0e6ff', borderRadius: 4, padding: '2px 4px', fontSize: 10, color: '#6b21a8', outline: 'none' };
            const setMvcField = (muscleKey, groupKey, field, val) => {
              setMvcByPart(prev => ({
                ...prev,
                [p.key]: {
                  ...(prev[p.key] || {}),
                  [muscleKey]: {
                    ...((prev[p.key] || {})[muscleKey] || {}),
                    [groupKey]: {
                      ...(((prev[p.key] || {})[muscleKey] || {})[groupKey] || {}),
                      [field]: val,
                    },
                  },
                },
              }));
            };
            return (
              <div key={p.key} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 2px 10px rgba(0,0,0,.06)', marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{p.label}</span>
                  {side && <span style={{ fontSize: 10, color: '#64748b' }}>({side === 'left' ? 'Left' : 'Right'})</span>}
                  <span style={{ fontSize: 9, background: p.metricType === 'both' ? '#fef3c7' : '#f3e8ff', color: p.metricType === 'both' ? '#b45309' : '#7b1fa2', borderRadius: 99, padding: '1px 7px', fontWeight: 700 }}>{p.metricType === 'both' ? 'ROM+MVC' : 'MVC'}</span>
                </div>
                {muscles.length === 0 ? (
                  <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 0', fontStyle: 'italic' }}>No muscles mapped for this body part</div>
                ) : (
                  <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                    {muscles.map(muscle => (
                      <div key={muscle.key} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#7b1fa2', padding: '4px 0 2px', borderBottom: '1px solid #f0e6ff' }}>
                          {muscle.icon} {muscle.label}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr>
                              <th style={{ ...TH, textAlign: 'left', color: '#374151', fontSize: 10 }}>Subgroup</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#7b1fa2', fontSize: 10 }}>Grade</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#7b1fa2', fontSize: 10 }}>Str(N)</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#7b1fa2', fontSize: 10 }}>Contract</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#7b1fa2', fontSize: 10 }}>Endur(s)</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#7b1fa2', fontSize: 10 }}>Fatigue</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#dc2626', fontSize: 10 }}>Pain</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#d97706', fontSize: 10 }}>Spasm</th>
                              <th style={{ ...TH, textAlign: 'center', color: '#64748b', fontSize: 10 }}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {muscle.groups.map((grp, gi) => {
                              const gd = mvcByPart[p.key]?.[muscle.key]?.[grp.key] || {};
                              return (
                                <tr key={grp.key} style={{ background: gi % 2 ? '#fafbfd' : '#fff' }}>
                                  <td style={{ ...TD, color: '#374151', fontSize: 10, whiteSpace: 'nowrap' }}>{grp.label}</td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <select value={gd.grade ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'grade', e.target.value)} style={mvcSelLt}>
                                      <option value="">—</option>
                                      <option value="0">0</option><option value="1">1</option><option value="2">2</option>
                                      <option value="3">3</option><option value="4">4</option><option value="5">5</option>
                                    </select>
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <input type="number" inputMode="decimal" min="0" max="999.9999" step="any" value={gd.strength ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'strength', e.target.value === '' ? undefined : Number(e.target.value))} style={mvcNumLt} />
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <select value={gd.contraction ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'contraction', e.target.value)} style={{ ...mvcSelLt, fontSize: 10 }}>
                                      <option value="">—</option>
                                      <option value="Normal">Normal</option><option value="Weak">Weak</option>
                                      <option value="Tremor">Tremor</option><option value="None">None</option>
                                    </select>
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <input type="number" min="0" max="999" value={gd.endurance ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'endurance', e.target.value === '' ? undefined : Number(e.target.value))} style={mvcNumLt} />
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <select value={gd.fatigue ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'fatigue', e.target.value)} style={{ ...mvcSelLt, fontSize: 10, width: 38 }}>
                                      <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                    </select>
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <input type="number" min="0" max="10" value={gd.pain ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'pain', e.target.value === '' ? undefined : Number(e.target.value))} style={{ ...mvcNumLt, width: 36, color: '#dc2626' }} />
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <select value={gd.spasm ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'spasm', e.target.value)} style={{ ...mvcSelLt, fontSize: 10, width: 38, color: '#d97706' }}>
                                      <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                    </select>
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    <input type="text" value={gd.notes ?? ''} onChange={e => setMvcField(muscle.key, grp.key, 'notes', e.target.value)} style={mvcTxtLt} placeholder="..." />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>Pain (0-10)</span>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setPainByPart(prev => ({ ...prev, [p.key]: Math.max(0, painV - 1) }))}
                    style={{ width: 24, height: 24, borderRadius: 5, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', fontSize: 15, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>−</button>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#dc2626', minWidth: 20, textAlign: 'center', flexShrink: 0 }}>{painV}</span>
                  <button onClick={() => setPainByPart(prev => ({ ...prev, [p.key]: Math.min(10, painV + 1) }))}
                    style={{ width: 24, height: 24, borderRadius: 5, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', fontSize: 15, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>+</button>
                </div>
              </div>
            );
          };

          const activeTab = romMvcTab;
          const romZero = romParts.filter(p => !hasRomValue(p));
          const romNonZero = romParts.filter(p => hasRomValue(p));
          const mvcZero = mvcParts.filter(p => !hasMvcValue(p));
          const mvcNonZero = mvcParts.filter(p => hasMvcValue(p));

          const cardGrid = (cards, compact = false) => (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
              {cards.map(p => activeTab === 'rom' ? renderRomCard(p, compact) : renderMvcCard(p, compact))}
            </div>
          );

          return (
            <div style={{ background: '#eef0f4', padding: '12px 14px 16px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              {/* Header + tab bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1c2e', paddingRight: 16, paddingBottom: 8 }}>🔬 ROM &amp; MVC Readings</span>
                {romParts.length > 0 && (
                  <button onClick={() => setRomMvcTab('rom')}
                    style={{ padding: '6px 18px', fontSize: 12, fontWeight: activeTab === 'rom' ? 700 : 500,
                      background: 'none', border: 'none', borderBottom: activeTab === 'rom' ? '2px solid #1565c0' : '2px solid transparent',
                      color: activeTab === 'rom' ? '#1565c0' : '#64748b', cursor: 'pointer', marginBottom: -1 }}>
                    ROM ({romParts.length})
                  </button>
                )}
                {mvcParts.length > 0 && (
                  <button onClick={() => setRomMvcTab('mvc')}
                    style={{ padding: '6px 18px', fontSize: 12, fontWeight: activeTab === 'mvc' ? 700 : 500,
                      background: 'none', border: 'none', borderBottom: activeTab === 'mvc' ? '2px solid #7b1fa2' : '2px solid transparent',
                      color: activeTab === 'mvc' ? '#7b1fa2' : '#64748b', cursor: 'pointer', marginBottom: -1 }}>
                    MVC ({mvcParts.length})
                  </button>
                )}
              </div>

              {/* ROM tab */}
              {activeTab === 'rom' && romParts.length > 0 && (
                <>
                  {cardGrid(romZero)}
                  {romNonZero.length > 0 && (
                    <>
                      <div onClick={() => setRomNonZeroOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#eff6ff', borderRadius: 8, cursor: 'pointer', border: '1px solid #bfdbfe', margin: '12px 0 0' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', flex: 1 }}>Already Recorded ({romNonZero.length})</span>
                        <span style={{ fontSize: 12, color: '#3b82f6' }}>{romNonZeroOpen ? '▴' : '▾'}</span>
                      </div>
                      {romNonZeroOpen && <div style={{ marginTop: 10 }}>{cardGrid(romNonZero, true)}</div>}
                    </>
                  )}
                </>
              )}

              {/* MVC tab */}
              {activeTab === 'mvc' && mvcParts.length > 0 && (
                <>
                  {cardGrid(mvcZero)}
                  {mvcNonZero.length > 0 && (
                    <>
                      <div onClick={() => setMvcNonZeroOpen(v => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#faf5ff', borderRadius: 8, cursor: 'pointer', border: '1px solid #d8b4fe', margin: '12px 0 0' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#7e22ce', flex: 1 }}>Already Recorded ({mvcNonZero.length})</span>
                        <span style={{ fontSize: 12, color: '#9333ea' }}>{mvcNonZeroOpen ? '▴' : '▾'}</span>
                      </div>
                      {mvcNonZeroOpen && <div style={{ marginTop: 10 }}>{cardGrid(mvcNonZero, true)}</div>}
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ── FOOTER PANEL ─────────────────────────────────────── */}
        <div className="bottomBar glass" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {/* Session status pill */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span className={appointment?.status === "inprogress" ? "dotGreen" : "dotOrange"} />
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {appointment?.status === "inprogress" ? `In Progress • ${formatTime(elapsedSec)}` : appointment?.status === "scheduled" ? "Scheduled" : appointment?.status === "Completed" ? "Completed ✅" : "No appointment"}
            </span>
            {appointment?.escalated && (
              <span style={{ fontSize: 10, color: '#ff6b35', fontWeight: 700, flexShrink: 0 }}>🔔 ESCALATED</span>
            )}
          </div>
          {/* Escalate button */}
          <button
            className="btn warn"
            onClick={handleEscalate}
            disabled={!appointment}
            style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
          >
            🔔 Escalate
          </button>
          {/* Mark Done */}
          {appointment?.status === "inprogress" && (
            <button
              className="btn primary"
              onClick={handleComplete}
              style={{ background: 'linear-gradient(135deg,#16a34a,#0d9488)', border: 'none', fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
            >
              <span className="iconCheck" /> Mark Done
            </button>
          )}
        </div>

        {/* ── ADJUSTMENTS MODAL ────────────────────────────────── */}
        {showAdjustments && (
          <AdjustmentsModal
            onClose={() => setShowAdjustments(false)}
            adjustments={currentAdjustments}
            onChange={setCurrentAdjustments}
          />
        )}

        {/* ── RECOMMENDATIONS PROMPT ───────────────────────────── */}
        {showRecommPrompt && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#1a2a3a', borderRadius: 16, padding: '24px 28px', maxWidth: 400, width: '100%', color: '#e2e8f0' }}>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>⚠️ Recommendations not answered</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>
                {sessionRecomms.filter(r => r.completed === null || r.completed === undefined).length} recommendation(s) have not been marked as Done or Skipped. Please scroll up and fill them in, or proceed anyway.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRecommPrompt(false)}
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, padding: '8px 16px', cursor: 'pointer' }}>
                  Go back & fill
                </button>
                <button
                  onClick={() => { setShowRecommPrompt(false); setShowFeedbackModal(true); }}
                  style={{ background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 16px', cursor: 'pointer' }}>
                  Proceed anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SESSION FEEDBACK MODAL ───────────────────────────── */}
        {showFeedbackModal && (
          <SessionFeedbackModal
            onSave={handleFeedbackSave}
            onCancel={() => setShowFeedbackModal(false)}
            bodyParts={
              // Use the consultation's treated body parts (not the ROM/MVC lab-reading parts)
              (consultation?.bodyParts || []).length > 0
                ? consultation.bodyParts
                    .filter(bp => !(consultation?.planExcludedParts || []).includes(bp.partKey))
                    .map(bp => ({ key: bp.partKey, label: bp.label || bp.partKey }))
                : sessionParts  // fallback for older consultations without bodyParts array
            }
            romByPart={romByPart}
            mvcByPart={mvcByPart}
            painByPart={painByPart}
            appointment={appointment}
            currentUser={auth?.username}
            exercises={exercises}
            lastSymptomsByPart={
              allSessions
                .filter(s => s.status === 'Completed' && s.sessionFeedback?.symptomsByPart)
                .sort((a, b) => new Date(b.start) - new Date(a.start))[0]
                ?.sessionFeedback?.symptomsByPart || null
            }
          />
        )}

        {/* ── EDIT EXERCISE MODAL ──────────────────────────────── */}
        {editExIdx !== null && exercises[editExIdx] && (
          <EditExerciseModal
            exercise={exercises[editExIdx]}
            onSave={(changes) => {
              updateExercise(editExIdx, changes);
              setEditExIdx(null);
            }}
            onClose={() => setEditExIdx(null)}
          />
        )}

        {/* ── ADD EXERCISE MODAL ───────────────────────────────── */}
        {showAddExercise && (
          <AddExerciseModal
            existingExercises={exercises}
            allConsultationExercises={consultationAllSeqs}
            onAdd={(ex) => {
              addExercise(ex);
              setShowAddExercise(false);
            }}
            onAddMultiple={(exList) => {
              exList.forEach(ex => addExercise(ex));
              setShowAddExercise(false);
            }}
            onClose={() => setShowAddExercise(false)}
          />
        )}

        {/* ── SKIPPED RESTART MODAL ────────────────────────────── */}
        {showSkippedRestart && (
          <SkippedRestartModal
            skipped={exercises.filter((e) => e.status === "Skipped")}
            completed={exercises.filter((e) => e.status === "Completed")}
            unassigned={consultationAllSeqs.filter(ce => !exercises.some(ex => ex.name === ce.name))}
            onRestart={(ids, extras, edited) => { restartSkipped(ids, extras, edited); setShowSkippedRestart(false); }}
            onClose={() => setShowSkippedRestart(false)}
          />
        )}

        {/* ── ESCALATION MODAL ─────────────────────────────────── */}
        {showEscalationModal && appointment && (
          <EscalationModal
            appointment={appointment}
            patientCard={patientCard}
            onClose={(success) => {
              setShowEscalationModal(false);
              if (success) setAppointment(prev => ({ ...prev, escalated: true }));
            }}
            refreshSequences={() => loadPatientData(patientCard)}
          />
        )}

        {/* ── SESSION HISTORY MODAL ────────────────────────────── */}
        {historySession && (
          <SessionHistoryModal
            session={historySession}
            allSessions={allSessions}
            onClose={() => setHistorySession(null)}
          />
        )}

        {/* ── FLOATING VIDEO PLAYER (only when popped out) ─────── */}
        {showVideoPlayer && appointment && videoPopped && (
          <SessionVideoPlayer
            ref={videoPlayerRef}
            patientCard={patientCard}
            onClose={() => { setShowVideoPlayer(false); setVideoPopped(false); }}
            onPopIn={() => setVideoPopped(false)}
          />
        )}

        {/* ── Audio element for exercise-complete ding ─────────── */}
        <audio ref={audioRef} src="/session-over-new.mp3" preload="auto" playsInline />

        {/* ── NEXT APPOINTMENT MODAL (shown after session completes) ── */}
        {showNextApptModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.82)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#1a2a3a', borderRadius: 18, padding: '24px 28px', width: '100%', maxWidth: 400, color: '#e2e8f0' }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4, color: '#fff' }}>✅ Session Complete!</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20, borderBottom: '1px solid #334155', paddingBottom: 12 }}>
                Schedule the next appointment for {profile?.name || patientCard}?
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date & Time</div>
                <input
                  type="datetime-local"
                  value={nextApptDate}
                  onChange={e => setNextApptDate(e.target.value)}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 14, padding: '8px 10px', boxSizing: 'border-box' }}
                />
              </div>
              <button
                onClick={() => {
                  const apptStart = new Date(appointment?.start || Date.now());
                  const tomorrow = new Date(apptStart);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const pad = n => String(n).padStart(2, '0');
                  setNextApptDate(`${tomorrow.getFullYear()}-${pad(tomorrow.getMonth()+1)}-${pad(tomorrow.getDate())}T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`);
                }}
                style={{ width: '100%', marginBottom: 12, padding: '8px 0', background: 'rgba(255,255,255,0.06)', border: '1px solid #334155', borderRadius: 8, color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>
                📅 Tomorrow, Same Time
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => scheduleNextAppointment(nextApptDate)}
                  disabled={!nextApptDate}
                  style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg,#2563eb,#0891b2)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: nextApptDate ? 'pointer' : 'not-allowed', opacity: nextApptDate ? 1 : 0.5 }}>
                  Schedule
                </button>
                <button
                  onClick={() => setShowNextApptModal(false)}
                  style={{ padding: '11px 18px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Skip
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DUPLICATE EXERCISE MODAL ── */}
        {showDuplicateModal && duplicateTargetIdx !== null && exercises[duplicateTargetIdx] && (
          <DuplicateExerciseModal
            exercise={exercises[duplicateTargetIdx]}
            onConfirm={(suffix, duration, repetition) => {
              duplicateExercise(duplicateTargetIdx, suffix, duration, repetition);
              setShowDuplicateModal(false);
              setDuplicateTargetIdx(null);
            }}
            onCancel={() => { setShowDuplicateModal(false); setDuplicateTargetIdx(null); }}
          />
        )}

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════════════ */

function ExerciseRow({ item, onClick, onDone, onSkip, onRemove, onEdit, onDuplicate, onRestart, onAddComment, isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const { auth } = useAuth();

  const commentLog = Array.isArray(item.commentLog) ? item.commentLog :
    (item.remarkText ? [{ date: 'Prior entry', author: '—', role: 'therapist', text: item.remarkText }] : []);

  // Auto-expand comments if any exist; user can manually collapse
  const [showComments, setShowComments] = React.useState(commentLog.length > 0);
  const [commentText, setCommentText] = React.useState('');

  const userRole = (auth?.roles || []).some(r => ['admin','superadmin','owner'].includes(r)) ? 'doctor' : 'therapist';

  const handleAddComment = (e) => {
    e.stopPropagation();
    if (!commentText.trim()) return;
    const now = new Date();
    const dateStr = now.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
    onAddComment?.({
      date: dateStr,
      author: auth?.username || auth?.name || 'User',
      role: userRole,
      text: commentText.trim(),
    });
    setCommentText('');
  };

  const cls = [
    'row',
    item.status === "done"   ? "done"   : '',
    item.status === "active" ? "active" : '',
    isDragging ? "dragging" : '',
    isDragOver ? "dragOver" : '',
  ].filter(Boolean).join(' ');

  return (
    <div style={{ position: 'relative' }}>
      <div
        className={cls}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onClick?.()}
        draggable
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(); }}
        onDragOver={(e) => { e.preventDefault(); onDragOver?.(); }}
        onDrop={(e) => { e.preventDefault(); onDrop?.(); }}
        onDragEnd={() => onDragEnd?.()}
      >
        {/* ── Row 1: Status + Name ── */}
        <div className="rowTop">
          <div className="rowLeft">
            {item.status === "done" ? (
              <span className="status done" aria-hidden="true">✓</span>
            ) : item.status === "active" ? (
              <span className="status play" aria-hidden="true">❯</span>
            ) : (
              <span className="status empty" aria-hidden="true" />
            )}
          </div>
          <div className="rowName">{item.title}</div>
          {item.delta1 && (
            <div className="deltas" style={{ flexShrink: 0 }}>
              <div className="delta green" style={{ fontSize: 11 }}>{item.delta1}</div>
            </div>
          )}
        </div>

        {/* ── Row 2: Pills + Action buttons ── */}
        <div className="rowBottom">
          <div className="rowPills">
            {item.duration != null && (
              <span style={{ fontSize: 11, color: '#6b9070', background: 'rgba(107,144,112,.14)', borderRadius: 5, padding: '1px 6px', fontWeight: 700 }}>
                ⏱{fmtDur(item.duration)}
              </span>
            )}
            {item.repetition != null && (
              <span style={{ fontSize: 11, color: '#b45309', background: 'rgba(204,138,43,.14)', borderRadius: 5, padding: '1px 6px', fontWeight: 700 }}>
                ×{item.repetition}
              </span>
            )}
            {item.status === 'done' && item.timeSpentSec != null && (
              <span style={{ fontSize: 11, color: '#7c3aed', background: 'rgba(124,58,237,.12)', borderRadius: 5, padding: '1px 6px', fontWeight: 700 }}>
                ⏱{fmtDur(item.timeSpentSec)} actual
              </span>
            )}
            {item.status === 'done' && item.repsCompleted != null && (
              <span style={{ fontSize: 11, color: '#0891b2', background: 'rgba(8,145,178,.12)', borderRadius: 5, padding: '1px 6px', fontWeight: 700 }}>
                ✓{item.repsCompleted}/{item.repetition} reps
              </span>
            )}
          </div>
          <div className="rowActions">
            {onRestart && (
              <button className="rowAction" title="Restart exercise (redo reps)"
                onClick={(e) => { e.stopPropagation(); onRestart(); }}
                style={{ color: '#f97316' }}>
                ↺
              </button>
            )}
            {onEdit && (
              <button className="rowAction" title="Edit duration & reps"
                onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                ✎
              </button>
            )}
            {onDuplicate && (
              <button className="rowAction" title="Duplicate exercise"
                onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                style={{ color: '#0369a1', borderColor: 'rgba(3,105,161,.30)', background: 'rgba(224,242,254,.95)' }}>
                ⊕
              </button>
            )}
            {onSkip && (
              <button className="rowAction warn" title="Skip"
                onClick={(e) => { e.stopPropagation(); onSkip(); }}>
                ↷
              </button>
            )}
            {onRemove && (
              <button className="rowAction danger" title="Remove"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}>
                ✕
              </button>
            )}
            <button
              className={`rowAction comment${showComments ? ' active' : ''}`}
              title="Comments"
              onClick={(e) => { e.stopPropagation(); setShowComments(v => !v); }}
              style={showComments ? { background: 'rgba(219,234,254,.90)' } : {}}>
              💬{commentLog.length > 0 ? ` ${commentLog.length}` : ''}
            </button>
          </div>
        </div>
      </div>

      {/* ── Comment log (expanded) ── */}
      {showComments && (
        <div className="commentLog">
          {commentLog.length === 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8', padding: '3px 4px', fontStyle: 'italic' }}>No comments yet.</div>
          )}
          {commentLog.map((c, i) => (
            <div key={i} className={`commentEntry ${c.role === 'doctor' ? 'doctor' : 'therapist'}`}>
              <div className={`commentMeta ${c.role === 'doctor' ? 'doctor' : 'therapist'}`}>
                [{c.date} • {c.author} • {c.role === 'doctor' ? 'Doctor' : 'Therapist'}]
              </div>
              <div className="commentText">{c.text}</div>
            </div>
          ))}
          <div className="commentInput">
            <input
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddComment(e); } }}
              placeholder="Add a note…"
              onClick={e => e.stopPropagation()}
            />
            <button className="commentAddBtn" onClick={handleAddComment}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DuplicateExerciseModal({ exercise, onConfirm, onCancel }) {
  const [suffix, setSuffix] = React.useState('');
  const [duration, setDuration] = React.useState(String(exercise?.duration ?? ''));
  const [repetition, setRepetition] = React.useState(String(exercise?.repetition ?? ''));

  const handleConfirm = () => {
    onConfirm(suffix.trim(), duration, repetition);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.82)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#1a2a3a', borderRadius: 16, padding: '22px 24px', width: '100%', maxWidth: 360, color: '#e2e8f0' }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: '#60a5fa' }}>⊕ Duplicate Exercise</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
          Duplicating: <strong style={{ color: '#fff' }}>{exercise?.name}</strong>
        </div>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 3 }}>
          Suffix (e.g. Left, Right, Upper)
        </label>
        <input
          value={suffix}
          onChange={e => setSuffix(e.target.value)}
          placeholder="Leave blank to keep same name"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && handleConfirm()}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.18)', background: '#0f1f2e', color: '#e2e8f0', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}
        />

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 3 }}>Duration (sec)</label>
            <input
              type="number" min="1"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.18)', background: '#0f1f2e', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 3 }}>Reps</label>
            <input
              type="number" min="1"
              value={repetition}
              onChange={e => setRepetition(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.18)', background: '#0f1f2e', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleConfirm}
            style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
            ⊕ Add Duplicate
          </button>
          <button
            onClick={onCancel}
            style={{ padding: '10px 16px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  title,
  value,
  note,
  bars = 5,
  activeBars = 3,
  chartType = "area",
  onDecrement,
  onIncrement,
}) {
  return (
    <div className="metric glass">
      <div className="metricTop">
        <div className="metricTitle">{title}</div>
      </div>

      <div className="metricMid">
        <div className="stepper" aria-hidden="true">
          <span
            className="stepBtn"
            onClick={onDecrement}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            –
          </span>
          <span className="stepVal">{value}</span>
          <span
            className="stepBtn"
            onClick={onIncrement}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            +
          </span>
        </div>

        <div className="barGroup" aria-hidden="true">
          <div className="barPills">
            {Array.from({ length: bars }).map((_, i) => (
              <div key={i} className={i < activeBars ? "bar on" : "bar"} />
            ))}
          </div>
          <div className="barNums">
            {Array.from({ length: bars }).map((_, i) => (
              <span key={i}>{i + 1}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="metricNote">{note}</div>

      <div className="miniChart" aria-hidden="true">
        {chartType === "area" ? (
          <div className="areaChart">
            <div className="grid" />
            <div className="areaFill" />
            <div className="areaLine" />
          </div>
        ) : (
          <div className="barsChart">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} className="b" style={{ height: `${10 + i * 4}%` }} />
            ))}
          </div>
        )}
        <div className="axis">
          <span>0–1</span>
          <span>2:04</span>
        </div>
      </div>
    </div>
  );
}

function AdjustmentsModal({ onClose, adjustments, onChange }) {
  const toggle = (label) => {
    onChange((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modal glassStrong">
        <div className="modalHeader">
          <div>
            <div className="modalTitle">Adjustments</div>
            <div className="modalSub">
              Document any adjustments made during this exercise session.
            </div>
          </div>
          <button className="xBtn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modalBody">
          {/* Position Modifications – 2 columns */}
          <ModalSection
            title="Position Modifications"
            left={[
              { label: "Extra pillow under knee",    checked: !!adjustments["Extra pillow under knee"] },
              { label: "Bolster support",             checked: !!adjustments["Bolster support"] },
              { label: "Wall support",                checked: !!adjustments["Wall support"] },
            ]}
            right={[
              { label: "Extra pillow under lumbar",  checked: !!adjustments["Extra pillow under lumbar"] },
              { label: "Strap support",               checked: !!adjustments["Strap support"] },
              { label: "Chair assisted",              checked: !!adjustments["Chair assisted"] },
            ]}
            onToggle={toggle}
          />

          <div className="modalGrid2">
            <ModalSection
              title="Range Modifications"
              left={[
                { label: "Reduced ROM",                   checked: !!adjustments["Reduced ROM"] },
                { label: "Assisted ROM",                  checked: !!adjustments["Assisted ROM"] },
                { label: "Partial hold only",             checked: !!adjustments["Partial hold only"] },
                { label: "Isometric instead of dynamic",  checked: !!adjustments["Isometric instead of dynamic"] },
              ]}
              onToggle={toggle}
            />
            <ModalSection
              title="Load Modifications"
              left={[
                { label: "Reduced hold time",   checked: !!adjustments["Reduced hold time"] },
                { label: "Increased hold time",  checked: !!adjustments["Increased hold time"] },
                { label: "Reduced reps",         checked: !!adjustments["Reduced reps"] },
                { label: "Added resistance",     checked: !!adjustments["Added resistance"] },
              ]}
              onToggle={toggle}
            />
          </div>

          <div className="modalGrid2">
            <ModalSection
              title="Pain-Based Modifications"
              left={[
                { label: "Stopped due to pain",        checked: !!adjustments["Stopped due to pain"] },
                { label: "Modified due to discomfort", checked: !!adjustments["Modified due to discomfort"] },
                { label: "Muscle fatigue early",       checked: !!adjustments["Muscle fatigue early"] },
              ]}
              onToggle={toggle}
            />

            <div className="customCard glass2">
              <div className="customPlus">＋</div>
              <div className="customText">Add Custom Adjustment</div>
            </div>
          </div>
        </div>

        <div className="modalFooter">
          <button className="btn primary wide" onClick={onClose}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalSection({ title, left = [], right = null, onToggle }) {
  return (
    <div className="modalSection glass2">
      <div className="modalSectionTitle">{title}</div>
      <div className={right ? "checksGrid" : "checks"}>
        <div className="checksCol">
          {left.map((x, i) => (
            <label key={i} className="checkRow">
              <input
                type="checkbox"
                checked={!!x.checked}
                onChange={() => onToggle?.(x.label)}
              />
              <span>{x.label}</span>
            </label>
          ))}
        </div>
        {right && (
          <div className="checksCol">
            {right.map((x, i) => (
              <label key={`r-${i}`} className="checkRow">
                <input
                  type="checkbox"
                  checked={!!x.checked}
                  onChange={() => onToggle?.(x.label)}
                />
                <span>{x.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LegFigure() {
  return (
    <svg viewBox="0 0 120 220" className="legSvg">
      <defs>
        <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#f1efe9" />
          <stop offset="1" stopColor="#d8d2c8" />
        </linearGradient>
      </defs>
      <path
        d="M76 10c-8 8-16 22-10 42 5 17-6 41-13 59-7 18-5 33 8 45 13 12 18 26 6 44-11 17-14 19-16 20 10 3 29-3 41-17 11-13 12-33 6-51-6-17-2-27 5-40 7-13 5-29-6-42-11-14-7-26-1-37 7-13 0-20-10-23z"
        fill="url(#g)"
        stroke="rgba(0,0,0,.10)"
      />
      <path
        d="M62 72c-8 14-8 25 1 36 9 11 24 11 31-2"
        fill="none"
        stroke="rgba(190,90,90,.55)"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path
        d="M64 122c-7 14-3 23 10 29 12 6 26 1 30-12"
        fill="none"
        stroke="rgba(190,90,90,.45)"
        strokeWidth="10"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════
   RECOMMENDATIONS GRAPH
   Shows per-recommendation completion (yes/no) + qty over sessions.
═══════════════════════════════════════════════════════════════ */
function RecommendationsGraph({ sessions }) {
  const completedSessions = (sessions || [])
    .filter(s => s.status === 'Completed' && Array.isArray(s.sessionFeedback?.recommendationTracking))
    .slice(-8); // last 8 sessions

  if (completedSessions.length === 0) {
    return (
      <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 4 }}>📈 Recommendation Progress</div>
        <div style={{ fontSize: 10, color: '#475569', textAlign: 'center', padding: '8px 0' }}>Complete sessions to see progress</div>
      </div>
    );
  }

  // Collect all unique recommendation names
  const allNames = [];
  completedSessions.forEach(s => {
    (s.sessionFeedback.recommendationTracking || []).forEach(r => {
      if (r.name && !allNames.includes(r.name)) allNames.push(r.name);
    });
  });

  const COLORS = ['#10b981','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#0891b2'];
  const W = 320, H = 80, PL = 8, PT = 8, PB = 18;

  return (
    <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 6 }}>📈 Recommendation Progress</div>
      {/* Session labels row */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 6, overflowX: 'auto' }}>
        {completedSessions.map((_, si) => (
          <div key={si} style={{ minWidth: 28, fontSize: 9, color: '#64748b', textAlign: 'center' }}>S{si + 1}</div>
        ))}
      </div>
      {allNames.map((name, ni) => {
        const pts = completedSessions.map((s, si) => {
          const tracked = (s.sessionFeedback.recommendationTracking || []).find(r => r.name === name);
          return tracked ? (tracked.completed === true ? 1 : tracked.completed === false ? 0 : 0.5) : 0.5;
        });
        return (
          <div key={ni} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <div style={{ width: 80, fontSize: 9, color: COLORS[ni % COLORS.length], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={name}>{name}</div>
            <div style={{ display: 'flex', gap: 2, flex: 1 }}>
              {pts.map((v, si) => (
                <div key={si} style={{ flex: 1, height: 14, borderRadius: 3, background: v === 1 ? '#16a34a' : v === 0 ? '#dc2626' : '#475569', opacity: 0.85 }} title={v === 1 ? 'Yes' : v === 0 ? 'No' : '?'} />
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <span style={{ fontSize: 9, color: '#16a34a' }}>■ Done</span>
        <span style={{ fontSize: 9, color: '#dc2626' }}>■ Skipped</span>
        <span style={{ fontSize: 9, color: '#475569' }}>■ Unknown</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SESSION HISTORY MODAL
   Full read-only detail of a past completed session.
═══════════════════════════════════════════════════════════════ */
function SessionHistoryModal({ session, allSessions, onClose }) {
  const sf = session.sessionFeedback || {};
  const snap = sf.exerciseSnapshot || [];
  const recTracking = sf.recommendationTracking || [];
  const adjs = sf.adjustmentsApplied || [];
  const romByPart = sf.romByPart || {};
  const mvcByPart = sf.mvcByPart || {};
  const painByPart = sf.painByPart || {};

  const dateStr = session.start
    ? new Date(session.start).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const timeStr = session.start
    ? new Date(session.start).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    : '';

  // Determine session number relative to all completed sessions (oldest first = #1)
  const completedSorted = allSessions
    .filter(s => s.status === 'Completed')
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const sessionNum = completedSorted.findIndex(s => s._id === session._id) + 1;

  const ov  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.88)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const bx  = { background: '#0f172a', borderRadius: 18, padding: '22px 24px', width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', color: '#e2e8f0', position: 'relative' };
  const sectionTitle = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '14px 0 6px' };
  const badge = (bg, color, text) => (
    <span style={{ background: bg, color, borderRadius: 5, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{text}</span>
  );

  const SYMPTOM_LABELS = { pain: 'Pain', numbness: 'Numbness', weakness: 'Weakness', stiffness: 'Stiffness', burning: 'Burning', spasm: 'Spasm', instability: 'Instability' };
  const SYMPTOM_COLORS = { pain: '#ef4444', numbness: '#f59e0b', weakness: '#f97316', stiffness: '#3b82f6', burning: '#ec4899', spasm: '#8b5cf6', instability: '#0891b2' };

  return (
    <div style={ov} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={bx}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#fff', marginBottom: 2 }}>
              📋 Session #{sessionNum} · {dateStr}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {timeStr}{session.username ? ` · ${session.username}` : ''}
              {session._id ? ` · #${session._id.slice(-5).toUpperCase()}` : ''}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: '#94a3b8', fontSize: 16, padding: '4px 10px', cursor: 'pointer', marginLeft: 10 }}>
            ✕
          </button>
        </div>

        {/* Therapists */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {session.leadTherapist && (
            <span style={{ fontSize: 11, background: 'rgba(59,130,246,0.14)', color: '#93c5fd', borderRadius: 6, padding: '2px 9px', fontWeight: 600 }}>
              Lead: {session.leadTherapist}
            </span>
          )}
          {session.helperTherapist1 && (
            <span style={{ fontSize: 11, background: 'rgba(99,102,241,0.14)', color: '#a5b4fc', borderRadius: 6, padding: '2px 9px', fontWeight: 600 }}>
              Support: {session.helperTherapist1}
            </span>
          )}
          {session.helperTherapist2 && (
            <span style={{ fontSize: 11, background: 'rgba(99,102,241,0.14)', color: '#a5b4fc', borderRadius: 6, padding: '2px 9px', fontWeight: 600 }}>
              Support: {session.helperTherapist2}
            </span>
          )}
        </div>

        {/* Session comments */}
        {session.sessionComments && (
          <div style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#cbd5e1', lineHeight: 1.55, marginBottom: 4 }}>
            💬 {session.sessionComments}
          </div>
        )}

        {/* Exercise Snapshot */}
        {snap.length > 0 && (
          <>
            <div style={sectionTitle}>Exercises ({snap.filter(e => e.status === 'Completed').length}/{snap.length} completed)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {snap.map((ex, i) => {
                const statusColor = ex.status === 'Completed' ? '#22c55e' : ex.status === 'Skipped' ? '#f59e0b' : '#64748b';
                const statusBg    = ex.status === 'Completed' ? 'rgba(34,197,94,0.10)' : ex.status === 'Skipped' ? 'rgba(245,158,11,0.10)' : 'rgba(100,116,139,0.10)';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: statusBg, borderRadius: 7, padding: '5px 10px', border: `1px solid ${statusColor}22` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', minWidth: 18 }}>{i + 1}.</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', flex: 1 }}>{ex.name || 'Exercise'}</span>
                    {badge(statusBg, statusColor, ex.status || '?')}
                    {ex.repsCompleted != null && (
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{ex.repsCompleted}/{ex.repetition} reps</span>
                    )}
                    {ex.timeSpentSec > 0 && (
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDur(ex.timeSpentSec)}</span>
                    )}
                    {ex.wasRestarted && (
                      <span title="Was restarted" style={{ fontSize: 10, color: '#f97316' }}>↺</span>
                    )}
                    {(ex.adjustments || []).length > 0 && (
                      <span title={ex.adjustments.join(', ')} style={{ fontSize: 10, color: '#fbbf24', cursor: 'help' }}>
                        ⚙ {ex.adjustments.length}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Exercise comments */}
        {snap.some(e => e.commentLog?.length > 0) && (
          <>
            <div style={sectionTitle}>Exercise Comments</div>
            {snap.filter(e => e.commentLog?.length > 0).map((ex, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', marginBottom: 2 }}>{ex.name}</div>
                {ex.commentLog.map((c, j) => (
                  <div key={j} style={{ fontSize: 11, color: '#cbd5e1', paddingLeft: 10, borderLeft: '2px solid #334155', marginBottom: 2 }}>
                    <span style={{ color: '#64748b' }}>{c.date} · {c.author}: </span>{c.text}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        {/* Body Part Readings */}
        {(Object.keys(romByPart).length > 0 || Object.keys(mvcByPart).length > 0 || Object.keys(painByPart).length > 0) && (
          <>
            <div style={sectionTitle}>Body Part Readings</div>
            {Array.from(new Set([...Object.keys(romByPart), ...Object.keys(mvcByPart), ...Object.keys(painByPart)])).map(k => {
              const rData = romByPart[k];
              const mData = mvcByPart[k];
              const pVal  = painByPart[k];
              const isRomObj = rData && typeof rData === 'object' && !Array.isArray(rData);
              const isMvcObj = mData && typeof mData === 'object' && !Array.isArray(mData);
              // Look up romKey from bpConfig or sessionParts
              const spItem = sessionParts.find(sp => sp.key === k);
              const clinicalKey = spItem?.romKey || '';
              const romPart = clinicalKey ? clinicalRomParts.find(p => p.key === clinicalKey) : null;
              return (
                <div key={k} style={{ marginBottom: 8, background: 'rgba(30,41,59,0.5)', borderRadius: 8, padding: '7px 10px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', marginBottom: 5 }}>
                    {SIV_PART_LABELS[k] || k}
                    {pVal != null && <span style={{ marginLeft: 8, fontSize: 10, color: '#fca5a5', fontWeight: 600 }}>Pain: {pVal}/10</span>}
                  </div>
                  {isRomObj && romPart && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 4 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', color: '#64748b', fontWeight: 600, padding: '2px 4px' }}>Movement</th>
                          <th style={{ textAlign: 'center', color: '#60a5fa', fontWeight: 600, padding: '2px 4px' }}>Active°</th>
                          <th style={{ textAlign: 'center', color: '#93c5fd', fontWeight: 600, padding: '2px 4px' }}>Passive°</th>
                          <th style={{ textAlign: 'center', color: '#fca5a5', fontWeight: 600, padding: '2px 4px' }}>Pain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {romPart.movements.map(move => {
                          const mv = rData[move.key];
                          if (!mv) return null;
                          return (
                            <tr key={move.key}>
                              <td style={{ color: '#94a3b8', padding: '2px 4px' }}>{move.label}</td>
                              <td style={{ textAlign: 'center', fontWeight: 700, color: '#60a5fa', padding: '2px 4px' }}>{mv.active != null ? `${mv.active}°` : '—'}</td>
                              <td style={{ textAlign: 'center', fontWeight: 700, color: '#93c5fd', padding: '2px 4px' }}>{mv.passive != null ? `${mv.passive}°` : '—'}</td>
                              <td style={{ textAlign: 'center', color: '#f87171', padding: '2px 4px' }}>{mv.pain ? '✓' : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {!isRomObj && rData != null && (
                    <div style={{ fontSize: 11, color: '#60a5fa', marginBottom: 4 }}>ROM: {rData}°</div>
                  )}
                  {isMvcObj && (
                    <div style={{ fontSize: 11, color: '#34d399' }}>
                      {Object.entries(mData).map(([muscleKey, groups]) => {
                        if (!groups || typeof groups !== 'object') return null;
                        const mLookup = buildMuscleLookup(clinicalMuscleParts);
                        const muscleDef = mLookup[muscleKey];
                        const grades = Object.entries(groups)
                          .map(([gk, gv]) => gv?.grade != null && gv.grade !== '' ? `${gk}: ${gv.grade}/5` : null)
                          .filter(Boolean);
                        if (grades.length === 0) return null;
                        return <div key={muscleKey} style={{ marginBottom: 2 }}>{muscleDef?.label || muscleKey}: {grades.join(', ')}</div>;
                      })}
                    </div>
                  )}
                  {!isMvcObj && mData != null && (
                    <div style={{ fontSize: 11, color: '#34d399' }}>MVC: {mData}/5</div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Symptom Scores */}
        {Object.keys(SYMPTOM_LABELS).some(k => sf[k] != null) && (
          <>
            <div style={sectionTitle}>Symptom Scores</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Object.entries(SYMPTOM_LABELS).map(([k, label]) => {
                if (sf[k] == null) return null;
                return (
                  <div key={k} style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 7, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: SYMPTOM_COLORS[k] || '#94a3b8', fontWeight: 600 }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{sf[k]}/10</span>
                  </div>
                );
              })}
              {sf.overallImprovement != null && (
                <div style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 7, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>Overall Improvement</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{sf.overallImprovement}/10</span>
                </div>
              )}
              {sf.recommendationScore != null && (
                <div style={{ background: 'rgba(51,65,85,0.55)', borderRadius: 7, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Recomm. Effectiveness</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{sf.recommendationScore}/10</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Recommendation Tracking */}
        {recTracking.length > 0 && (
          <>
            <div style={sectionTitle}>Recommendations</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {recTracking.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                  <span style={{ flex: 1, color: '#cbd5e1' }}>{r.name}</span>
                  {r.completed === true  && badge('rgba(34,197,94,0.15)',  '#22c55e', '✓ Done')}
                  {r.completed === false && badge('rgba(239,68,68,0.15)',  '#f87171', '✗ Skipped')}
                  {r.completed == null   && badge('rgba(100,116,139,0.15)','#64748b', '— N/A')}
                  {r.qty != null && r.qty !== '' && (
                    <span style={{ color: '#94a3b8' }}>qty: {r.qty}</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Adjustments Applied */}
        {adjs.length > 0 && (
          <>
            <div style={sectionTitle}>Adjustments Applied</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {adjs.map((a, i) => (
                <span key={i} style={{ fontSize: 10, background: 'rgba(245,158,11,0.12)', color: '#fcd34d', borderRadius: 5, padding: '2px 8px', fontWeight: 600 }}>
                  {a}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Notes */}
        {sf.notes && (
          <>
            <div style={sectionTitle}>Additional Notes</div>
            <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '4px 0' }}>{sf.notes}</div>
          </>
        )}

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, color: '#94a3b8', fontSize: 13, padding: '8px 20px', cursor: 'pointer', fontWeight: 600 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SESSION FEEDBACK MODAL
   Captures therapist-reported symptom levels + recommendation
   effectiveness at end of session.
═══════════════════════════════════════════════════════════════ */
const FEEDBACK_SYMPTOMS = [
  { key: 'pain',        label: 'Pain',        color: '#ef4444' },
  { key: 'numbness',    label: 'Numbness',    color: '#f59e0b' },
  { key: 'weakness',    label: 'Weakness',    color: '#f97316' },
  { key: 'stiffness',   label: 'Stiffness',   color: '#3b82f6' },
  { key: 'burning',     label: 'Burning',     color: '#ec4899' },
  { key: 'spasm',       label: 'Spasm',       color: '#8b5cf6' },
  { key: 'instability', label: 'Instability', color: '#0891b2' },
];

const SESSION_COMMENT_PRESETS = [
  'Session completed as planned',
  'Saadhak progressing well',
  'Exercise modified due to discomfort',
  'Saadhak reported increased pain during session',
  'Home exercises reviewed and demonstrated',
  'Reassessment done today',
  'Session shortened due to Saadhak fatigue',
  'New exercise introduced',
  'Pain reduced post-session',
  'Saadhak needs more practice at home',
  'Technique correction given',
  'Breathing correction given',
  'Saadhak reported no improvement',
];

function SessionFeedbackModal({ onSave, onCancel, bodyParts, romByPart, mvcByPart, painByPart, appointment, currentUser, exercises, lastSymptomsByPart }) {
  const partsList = bodyParts && bodyParts.length > 0 ? bodyParts : null;
  const GENERAL_KEY = '__general__';

  const [symptomsByPart, setSymptomsByPart] = React.useState(() => {
    const keys = partsList ? partsList.map(p => p.key) : [GENERAL_KEY];
    const init = {};
    keys.forEach(k => {
      init[k] = {};
      FEEDBACK_SYMPTOMS.forEach(s => { init[k][s.key] = lastSymptomsByPart?.[k]?.[s.key] ?? 5; });
    });
    return init;
  });
  const [selectedPart, setSelectedPart] = React.useState(
    partsList ? partsList[0].key : GENERAL_KEY
  );

  const [recScore, setRecScore]       = React.useState(5);
  const [improvement, setImprovement] = React.useState(5);
  const [notes, setNotes]             = React.useState('');

  // Therapist fields
  const [leadTherapist, setLeadTherapist] = React.useState(
    appointment?.leadTherapist || currentUser || ''
  );
  const [helper1, setHelper1] = React.useState(appointment?.helperTherapist1 || '');
  const [helper2, setHelper2] = React.useState(appointment?.helperTherapist2 || '');
  const [staffList, setStaffList]     = React.useState([]);

  // Session comments (required)
  const [sessionComments, setSessionComments] = React.useState('');
  const [commentsError, setCommentsError]     = React.useState(false);

  // Actual start/end times (pre-filled from appointment)
  const toLocalDT = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };
  const [actualStart, setActualStart] = React.useState(toLocalDT(appointment?.startTime || appointment?.start));
  const [actualEnd,   setActualEnd]   = React.useState(toLocalDT(appointment?.end));

  // Bulk mark remaining exercises
  const remainingCount = (exercises || []).filter(e => e.status !== 'Completed' && e.status !== 'Skipped').length;
  const [bulkMarkStatus, setBulkMarkStatus] = React.useState(null); // null | 'Completed' | 'Skipped'

  React.useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/users/therapist`)
      .then(r => setStaffList(r.data || []))
      .catch(() => {});
  }, []);

  const setPartScore = (symKey, val) =>
    setSymptomsByPart(prev => ({
      ...prev,
      [selectedPart]: { ...(prev[selectedPart] || {}), [symKey]: Number(val) },
    }));

  const appendPreset = (text) => {
    setSessionComments(prev => prev ? prev + '. ' + text : text);
    setCommentsError(false);
  };

  const handleSave = () => {
    if (!sessionComments.trim()) {
      setCommentsError(true);
      return;
    }
    const selectedTherapist = staffList.find(u => u.username === leadTherapist);
    onSave({
      symptomsByPart,
      recommendationScore: recScore,
      overallImprovement: improvement,
      notes,
      leadTherapist,
      leadTherapistDesignation: selectedTherapist?.designation || '',
      helperTherapist1: helper1,
      helperTherapist2: helper2,
      sessionComments: sessionComments.trim(),
      actualStartTime: actualStart || undefined,
      actualEndTime:   actualEnd   || undefined,
      bulkMarkStatus:  bulkMarkStatus || undefined,
    });
  };

  const ov  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.82)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const bx  = { background: '#1a2a3a', borderRadius: 18, padding: '22px 26px', width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', color: '#e2e8f0' };
  const lbl = { fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const sel = { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13, padding: '8px 10px', boxSizing: 'border-box' };

  const therapistOptions = staffList.length > 0
    ? staffList.map(u => <option key={u.username || u._id} value={u.username}>{u.name || u.username}</option>)
    : null;

  return (
    <div style={ov}>
      <div style={bx}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4, color: '#fff' }}>📋 Save & Complete Session</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18, borderBottom: '1px solid #334155', paddingBottom: 12 }}>
          Fill in session details before completing
        </div>

        {/* ── Bulk-mark remaining exercises ── */}
        {remainingCount > 0 && (
          <div style={{ marginBottom: 16, padding: '10px 12px', background: '#0f172a', borderRadius: 10, border: '1px solid #334155' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              ⚠ {remainingCount} exercise{remainingCount > 1 ? 's' : ''} not completed — mark remaining as:
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setBulkMarkStatus(s => s === 'Completed' ? null : 'Completed')}
                style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  background: bulkMarkStatus === 'Completed' ? '#16a34a' : 'rgba(22,163,74,0.15)',
                  color:      bulkMarkStatus === 'Completed' ? '#fff'    : '#4ade80' }}>
                ✓ Completed
              </button>
              <button
                onClick={() => setBulkMarkStatus(s => s === 'Skipped' ? null : 'Skipped')}
                style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  background: bulkMarkStatus === 'Skipped' ? '#d97706' : 'rgba(217,119,6,0.15)',
                  color:      bulkMarkStatus === 'Skipped' ? '#fff'    : '#fbbf24' }}>
                ↷ Skipped
              </button>
              <button
                onClick={() => setBulkMarkStatus(null)}
                style={{ padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 11,
                  background: 'rgba(255,255,255,0.05)', color: '#64748b' }}>
                Keep as-is
              </button>
            </div>
          </div>
        )}

        {/* ── Actual start / end times ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div>
            <div style={{ ...lbl, marginBottom: 5 }}>Actual Start Time</div>
            <input type="datetime-local" value={actualStart} onChange={e => setActualStart(e.target.value)}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 12, padding: '7px 9px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ ...lbl, marginBottom: 5 }}>Actual End Time</div>
            <input type="datetime-local" value={actualEnd} onChange={e => setActualEnd(e.target.value)}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 12, padding: '7px 9px', boxSizing: 'border-box' }} />
          </div>
        </div>

        {/* ── Therapists ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 18 }}>
          <div>
            <div style={lbl}>Lead Therapist ✱</div>
            <select style={sel} value={leadTherapist} onChange={e => setLeadTherapist(e.target.value)}>
              <option value="">— Select —</option>
              {therapistOptions}
              {/* Fallback: keep current value even if not in list yet */}
              {leadTherapist && !staffList.some(u => u.username === leadTherapist) && (
                <option value={leadTherapist}>{leadTherapist}</option>
              )}
            </select>
          </div>
          <div>
            <div style={lbl}>Support Therapist 1</div>
            <select style={sel} value={helper1} onChange={e => setHelper1(e.target.value)}>
              <option value="">— None —</option>
              {therapistOptions}
            </select>
          </div>
          <div>
            <div style={lbl}>Support Therapist 2</div>
            <select style={sel} value={helper2} onChange={e => setHelper2(e.target.value)}>
              <option value="">— None —</option>
              {therapistOptions}
            </select>
          </div>
        </div>

        {/* ── Session Comments (required) ── */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
            <div style={{ ...lbl, marginBottom: 0 }}>Session Comments <span style={{ color: '#ef4444' }}>✱ required</span></div>
          </div>
          {/* Quick-pick chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {SESSION_COMMENT_PRESETS.map(p => (
              <button
                key={p}
                onClick={() => appendPreset(p)}
                style={{
                  padding: '3px 9px', fontSize: 10.5, borderRadius: 99, cursor: 'pointer', fontWeight: 600,
                  background: sessionComments.includes(p) ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${sessionComments.includes(p) ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                  color: sessionComments.includes(p) ? '#93c5fd' : '#94a3b8',
                  transition: 'all 0.15s',
                }}
              >
                {p}
              </button>
            ))}
          </div>
          <textarea
            value={sessionComments}
            onChange={e => { setSessionComments(e.target.value); if (e.target.value.trim()) setCommentsError(false); }}
            placeholder="Tap chips above to add, or type freely…"
            style={{
              width: '100%', minHeight: 72, background: '#0f172a',
              border: `1.5px solid ${commentsError ? '#ef4444' : '#334155'}`,
              borderRadius: 8, color: '#e2e8f0', fontSize: 13,
              padding: '8px 10px', resize: 'vertical', boxSizing: 'border-box',
              outline: commentsError ? '2px solid rgba(239,68,68,0.3)' : 'none',
            }}
          />
          {commentsError && (
            <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>
              ⚠ Session Comments are required before completing.
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #334155', margin: '4px 0 16px' }} />

        {/* ── Body-part chips (clickable filter for symptom sliders) ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...lbl, marginBottom: 7 }}>
            Select body part to rate symptoms
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {(partsList || [{ key: GENERAL_KEY, label: 'General' }]).map(({ key, label }) => {
              const isActive = selectedPart === key;
              const r = romByPart?.[key], m = mvcByPart?.[key], p = painByPart?.[key];
              // Build summary for chip: count recorded ROM movements; MVC grade; pain
              const romCount = r && typeof r === 'object' ? Object.values(r).filter(mv => mv?.active != null || mv?.passive != null).length : (r != null ? 1 : 0);
              // Support new nested MVC structure: { muscleKey: { groupKey: { grade, ... } } }
              let mvcGrade = null;
              if (m && typeof m === 'object') {
                outer: for (const muscle of Object.values(m)) {
                  if (muscle && typeof muscle === 'object') {
                    for (const grp of Object.values(muscle)) {
                      if (grp?.grade != null && grp.grade !== '') { mvcGrade = grp.grade; break outer; }
                    }
                  }
                }
              }
              const hasData = romCount > 0 || mvcGrade != null || p != null;
              return (
                <button key={key} onClick={() => setSelectedPart(key)} style={{
                  padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  border: `1.5px solid ${isActive ? '#3b82f6' : '#334155'}`,
                  background: isActive ? 'rgba(59,130,246,0.2)' : '#0f172a',
                  color: isActive ? '#93c5fd' : '#94a3b8',
                  transition: 'all 0.15s',
                }}>
                  {label}
                  {hasData && (
                    <span style={{ marginLeft: 5, fontSize: 9, color: '#64748b', fontWeight: 500 }}>
                      {romCount > 0 && `ROM:${romCount}mv `}{mvcGrade != null && `MVC:${mvcGrade} `}{p != null && `P:${p}`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Symptom sliders for selected body part ── */}
        {selectedPart && (
          <div style={{ background: '#0f172a', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            {partsList && (
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginBottom: 12,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                📍 {partsList.find(p => p.key === selectedPart)?.label || selectedPart}
              </div>
            )}
            {FEEDBACK_SYMPTOMS.map(sym => {
              const val = symptomsByPart[selectedPart]?.[sym.key] ?? 5;
              return (
                <div key={sym.key} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: sym.color }}>{sym.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', background: '#1e293b', borderRadius: 6, padding: '1px 8px' }}>{val}</span>
                  </div>
                  <input type="range" min={0} max={10} step={1} value={val}
                    onChange={e => setPartScore(sym.key, e.target.value)}
                    style={{ width: '100%', accentColor: sym.color }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#64748b' }}>
                    <span>None</span><span>Mild</span><span>Moderate</span><span>Severe</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ borderTop: '1px solid #334155', margin: '14px 0' }} />

        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>Recommendation Effectiveness</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', background: '#0f172a', borderRadius: 6, padding: '1px 8px' }}>{recScore}/10</span>
          </div>
          <input type="range" min={0} max={10} step={1} value={recScore}
            onChange={e => setRecScore(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#10b981' }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>Overall Improvement (saadhak reported)</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', background: '#0f172a', borderRadius: 6, padding: '1px 8px' }}>{improvement}/10</span>
          </div>
          <input type="range" min={0} max={10} step={1} value={improvement}
            onChange={e => setImprovement(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#6366f1' }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ ...lbl, marginBottom: 6 }}>Additional Notes (optional)</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Any extra observations not covered above…"
            style={{ width: '100%', minHeight: 52, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 12, padding: '8px 10px', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave}
            style={{ flex: 1, padding: '12px 0', background: 'linear-gradient(135deg,#16a34a,#0d9488)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
            ✓ Save & Complete Session
          </button>
          <button onClick={onCancel}
            style={{ padding: '12px 18px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EDIT EXERCISE MODAL
   Only allows changing duration and reps of an upcoming exercise.
═══════════════════════════════════════════════════════════════ */
function EditExerciseModal({ exercise, onSave, onClose }) {
  // duration stored as seconds; decompose into m/s for editing
  const initSec = Number(exercise.duration) || 60;
  const [durM, setDurM] = useState(Math.floor(initSec / 60));
  const [durS, setDurS] = useState(initSec % 60);
  const [laps, setLaps] = useState(exercise.repetition || 1);

  const ov  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const bx  = { background: '#1a2a3a', borderRadius: 16, padding: '22px 26px', width: '100%', maxWidth: 360, color: '#e2e8f0' };
  const lbl = { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 5 };
  const inp = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 16, padding: '8px 6px', textAlign: 'center', fontWeight: 700, width: '100%', boxSizing: 'border-box' };
  const unit = { fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 2 };

  const totalSec = durM * 60 + durS;

  return (
    <div style={ov}>
      <div style={bx}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: '#fff' }}>✎ Edit Exercise</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18, borderBottom: '1px solid #334155', paddingBottom: 10 }}>
          {exercise.name || 'Exercise'}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={lbl}>Duration per Rep</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <input type="number" min={0} max={999} style={inp} value={durM}
                onChange={e => setDurM(Math.max(0, Number(e.target.value)))} />
              <div style={unit}>min</div>
            </div>
            <div>
              <input type="number" min={0} max={59} style={inp} value={durS}
                onChange={e => setDurS(Math.max(0, Math.min(59, Number(e.target.value))))} />
              <div style={unit}>sec</div>
            </div>
          </div>
          {totalSec > 0 && (
            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4, textAlign: 'center' }}>
              = {fmtDur(totalSec)} per rep
            </div>
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={lbl}>Reps / Laps</div>
          <input type="number" min={1} max={50} style={inp} value={laps}
            onChange={e => setLaps(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onSave({ duration: Math.max(1, totalSec), durationUnit: 'seconds', repetition: Number(laps) })}
            style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(135deg,#3b82f6,#0891b2)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            ✓ Save
          </button>
          <button onClick={onClose} style={{ padding: '10px 16px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ADD EXERCISE MODAL
   New exercise, duplicate existing, or pick from remaining list
   (with search, duration filter, and multi-select checkboxes).
═══════════════════════════════════════════════════════════════ */
function AddExerciseModal({ existingExercises, allConsultationExercises, onAdd, onAddMultiple, onClose }) {
  const [mode, setMode]       = useState('remaining'); // default to list
  const [name, setName]       = useState('');
  const [sourceIdx, setSourceIdx] = useState(0);
  const [variant, setVariant] = useState('Standard');
  // duration stored as total seconds; 60 = 1 min default
  const [duration, setDuration] = useState(60);
  const [newDurM, setNewDurM] = useState(1);
  const [newDurS, setNewDurS] = useState(0);
  const [laps, setLaps]       = useState(1);
  const [comments, setComments] = useState('');

  // "From List" multi-select state
  const [search, setSearch]         = useState('');
  const [durFilter, setDurFilter]   = useState('all'); // 'all' | 'short' | 'medium' | 'long'
  const [selectedSet, setSelectedSet] = useState(new Set()); // indices into filtered list

  // Fallback: fetch global exercises master list if consultation has none
  const [masterExercises, setMasterExercises] = useState([]);
  useEffect(() => {
    if ((allConsultationExercises || []).length === 0) {
      api.get(process.env.REACT_APP_URL + '/exercises')
        .then(r => setMasterExercises(r.data || []))
        .catch(() => setMasterExercises([]));
    }
  }, [allConsultationExercises]);

  const sourceEx = existingExercises[sourceIdx] || {};
  const exercisePool = (allConsultationExercises || []).length > 0
    ? allConsultationExercises
    : masterExercises;
  const remainingExercises = exercisePool
    .filter(ce => !existingExercises.some(ex => ex.name === ce.name));

  const filteredExercises = remainingExercises.filter(ex => {
    const matchSearch = !search || (ex.name || '').toLowerCase().includes(search.toLowerCase());
    const dur = Number(ex.duration) || 60; // duration in seconds
    const matchDur = durFilter === 'all'
      ? true
      : durFilter === 'short'  ? dur <= 180              // ≤ 3 min
      : durFilter === 'medium' ? dur > 180 && dur <= 480 // 3–8 min
      : dur > 480;                                       // > 8 min
    return matchSearch && matchDur;
  });

  const toggleSelect = (i) => {
    setSelectedSet(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  const selectAll  = () => setSelectedSet(new Set(filteredExercises.map((_, i) => i)));
  const clearAll   = () => setSelectedSet(new Set());

  const handleAdd = () => {
    if (mode === 'remaining') {
      if (selectedSet.size === 0) { showAlert('Select at least one exercise', 'warning'); return; }
      const exList = Array.from(selectedSet).map(i => filteredExercises[i]).filter(Boolean).map(ex => {
        return { name: ex.name, duration: ex.duration || 1, repetition: ex.repetition || 1, remarks: ex.remarks, comments: ex.comments };
      });
      if (!exList.length) { showAlert('Selected exercises no longer available', 'warning'); return; }
      onAddMultiple(exList);
      return;
    }
    const baseName = mode === 'duplicate'
      ? (sourceEx.name || 'Exercise').replace(/ \((Left|Right|Rotation-Left|Rotation-Right|Bilateral)\)$/, '')
      : (name.trim() || 'Exercise');
    const fullName = variant === 'Standard' ? baseName : `${baseName} (${variant})`;
    const newSec = newDurM * 60 + newDurS;
    const dur  = mode === 'duplicate' ? (sourceEx.duration  || duration) : Math.max(1, newSec);
    const reps = mode === 'duplicate' ? (sourceEx.repetition || laps)    : Number(laps);
    onAdd({ name: fullName, duration: dur, repetition: reps, comments, variant });
  };

  const ov  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.82)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const bx  = { background: '#1a2a3a', borderRadius: 16, padding: '18px 20px', width: '100%', maxWidth: 480, color: '#e2e8f0', maxHeight: '92vh', overflowY: 'auto' };
  const lbl = { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 4 };
  const inp = { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13, padding: '7px 10px', boxSizing: 'border-box' };

  return (
    <div style={ov}>
      <div style={bx}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 14, color: '#fff' }}>+ Add Exercise</div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {[['remaining', 'From List'], ['new', 'New'], ['duplicate', 'Duplicate']].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: mode === m ? '#3b82f6' : '#1e3a5f', color: mode === m ? '#fff' : '#94a3b8', border: 'none',
            }}>{label}</button>
          ))}
        </div>

        {/* ── FROM LIST (multi-select with search + filter) ─────── */}
        {mode === 'remaining' && (
          <>
            {/* Search + filter row */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                style={{ ...inp, flex: 1 }}
                value={search}
                onChange={e => { setSearch(e.target.value); setSelectedSet(new Set()); }}
                placeholder="🔍 Search exercises…"
              />
              <select
                value={durFilter}
                onChange={e => { setDurFilter(e.target.value); setSelectedSet(new Set()); }}
                style={{ ...inp, width: 'auto', padding: '7px 8px', flexShrink: 0 }}
              >
                <option value="all">All durations</option>
                <option value="short">≤3 min</option>
                <option value="medium">4–8 min</option>
                <option value="long">&gt;8 min</option>
              </select>
            </div>

            {/* Select all / Clear row */}
            {filteredExercises.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#64748b' }}>{filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={selectAll} style={{ fontSize: 11, background: 'none', border: '1px solid #3b82f6', color: '#3b82f6', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Select All</button>
                  <button onClick={clearAll}  style={{ fontSize: 11, background: 'none', border: '1px solid #475569', color: '#94a3b8', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Clear</button>
                </div>
              </div>
            )}

            {/* Checkbox list */}
            <div style={{ maxHeight: 240, overflowY: 'auto', borderRadius: 8, border: '1px solid #334155', marginBottom: 12 }}>
              {filteredExercises.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12, padding: 12, textAlign: 'center' }}>
                  {remainingExercises.length === 0
                    ? (exercisePool.length === 0
                      ? 'No exercises found. Use "New" tab to create one.'
                      : 'All exercises are already in this session.')
                    : 'No matches. Try a different search.'}
                </div>
              ) : filteredExercises.map((ex, i) => (
                <label key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  borderBottom: '1px solid #1e3a5f', cursor: 'pointer',
                  background: selectedSet.has(i) ? 'rgba(59,130,246,0.14)' : 'transparent',
                  transition: 'background 0.15s',
                }}>
                  <input type="checkbox" checked={selectedSet.has(i)} onChange={() => toggleSelect(i)}
                    style={{ width: 15, height: 15, accentColor: '#3b82f6', cursor: 'pointer', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: '#e2e8f0', fontWeight: selectedSet.has(i) ? 700 : 400 }}>{ex.name || 'Exercise'}</span>
                  <span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>{fmtDur(ex.duration || 60)} × {ex.repetition || 1}</span>
                  {(ex.remarks || ex.comments) && (
                    <span title={ex.remarks || ex.comments} style={{ fontSize: 10, color: '#64748b', cursor: 'help' }}>💬</span>
                  )}
                </label>
              ))}
            </div>

            {/* Selected count badge */}
            {selectedSet.size > 0 && (
              <div style={{ fontSize: 12, color: '#3b82f6', marginBottom: 8, fontWeight: 600 }}>
                ✓ {selectedSet.size} exercise{selectedSet.size !== 1 ? 's' : ''} selected
              </div>
            )}
          </>
        )}

        {/* ── NEW EXERCISE ──────────────────────────────────────── */}
        {mode === 'new' && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Exercise Name</div>
              <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Knee Flexion" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Variant</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {VARIANTS.map(v => (
                  <button key={v} onClick={() => setVariant(v)} style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: variant === v ? '#3b82f6' : '#1e3a5f', color: variant === v ? '#fff' : '#94a3b8',
                    border: '1px solid ' + (variant === v ? '#3b82f6' : '#334155'),
                  }}>{v}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Duration per Rep</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, alignItems: 'center' }}>
                <div>
                  <input type="number" min={0} max={999} style={inp} value={newDurM}
                    onChange={e => setNewDurM(Math.max(0, Number(e.target.value)))} />
                  <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 2 }}>min</div>
                </div>
                <div>
                  <input type="number" min={0} max={59} style={inp} value={newDurS}
                    onChange={e => setNewDurS(Math.max(0, Math.min(59, Number(e.target.value))))} />
                  <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 2 }}>sec</div>
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700 }}>
                  ={fmtDur(newDurM*60+newDurS)}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Laps / Reps</div>
              <input type="number" min={1} max={50} style={inp} value={laps} onChange={e => setLaps(e.target.value)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Comments (optional)</div>
              <textarea style={{ ...inp, minHeight: 48, resize: 'vertical' }} value={comments}
                onChange={e => setComments(e.target.value)} placeholder="Any instructions or notes…" />
            </div>
          </>
        )}

        {/* ── DUPLICATE ─────────────────────────────────────────── */}
        {mode === 'duplicate' && existingExercises.length > 0 && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Source Exercise</div>
              <select style={inp} value={sourceIdx} onChange={e => setSourceIdx(Number(e.target.value))}>
                {existingExercises.map((ex, i) => (
                  <option key={i} value={i}>{ex.name || 'Exercise'} ({fmtDur(ex.duration || 60)} × {ex.repetition || 1})</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Variant</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {VARIANTS.map(v => (
                  <button key={v} onClick={() => setVariant(v)} style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: variant === v ? '#3b82f6' : '#1e3a5f', color: variant === v ? '#fff' : '#94a3b8',
                    border: '1px solid ' + (variant === v ? '#3b82f6' : '#334155'),
                  }}>{v}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Comments (optional)</div>
              <textarea style={{ ...inp, minHeight: 48, resize: 'vertical' }} value={comments}
                onChange={e => setComments(e.target.value)} placeholder="Any instructions or notes…" />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleAdd} style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(135deg,#16a34a,#0891b2)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {mode === 'remaining' ? `+ Add ${selectedSet.size || ''} to Session` : '+ Add to Session'}
          </button>
          <button onClick={onClose} style={{ padding: '10px 16px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SKIPPED RESTART MODAL
   Restore skipped exercises and optionally add new ones or
   pick from completed exercises to repeat.
═══════════════════════════════════════════════════════════════ */
function SkippedRestartModal({ skipped, completed, unassigned, onRestart, onClose }) {
  const [selected, setSelected]         = useState(skipped.map(e => e._localId));
  const [showCompleted, setShowCompleted] = useState(false);
  const [pickedCompleted, setPickedCompleted] = useState([]);
  const [showAddNew, setShowAddNew]     = useState(false);
  const [extras, setExtras]             = useState([]);
  const [newName, setNewName]           = useState('');
  const [newVariant, setNewVariant]     = useState('Standard');
  const [newDuration, setNewDuration]   = useState(1);
  const [newLaps, setNewLaps]           = useState(1);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [pickedUnassigned, setPickedUnassigned] = useState([]);
  const [editableSkipped, setEditableSkipped] = useState(
    skipped.map(e => {
      const sec = Number(e.duration) || 60;
      return {
        ...e,
        editH: Math.floor(sec / 3600),
        editM: Math.floor((sec % 3600) / 60),
        editS: sec % 60,
        editReps: Number(e.repetition) || 1,
      };
    })
  );

  const toggleSelected  = (id) => setSelected(prev  => prev.includes(id)  ? prev.filter(x => x !== id)  : [...prev, id]);
  const toggleCompleted = (id) => setPickedCompleted(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const addNewExtra = () => {
    if (!newName.trim()) return;
    const fullName = newVariant === 'Standard' ? newName.trim() : `${newName.trim()} (${newVariant})`;
    setExtras(prev => [...prev, { name: fullName, duration: Number(newDuration), repetition: Number(newLaps), variant: newVariant }]);
    setNewName(''); setNewVariant('Standard'); setNewDuration(1); setNewLaps(1);
    setShowAddNew(false);
  };

  const handleRestart = () => {
    const restoredEdited = editableSkipped
      .filter(e => selected.includes(e._localId))
      .map(e => ({
        ...e,
        duration: (Number(e.editH) || 0) * 3600 + (Number(e.editM) || 0) * 60 + (Number(e.editS) || 0),
        repetition: e.editReps,
      }));
    const fromCompleted = completed
      .filter(e => pickedCompleted.includes(e._localId))
      .map(e => ({ name: e.name, duration: e.duration, repetition: e.repetition, variant: e.variant }));
    const fromUnassigned = (unassigned || [])
      .filter((_, i) => pickedUnassigned.includes(i))
      .map(e => ({ name: e.name, duration: e.duration, repetition: e.repetition, variant: e.variant }));
    onRestart(selected, [...extras, ...fromCompleted, ...fromUnassigned], restoredEdited);
  };

  const total = selected.length + pickedCompleted.length + extras.length + pickedUnassigned.length;
  const ov  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.82)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const bx  = { background: '#1a2a3a', borderRadius: 18, padding: '20px 24px', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', color: '#e2e8f0' };
  const lbl = { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 4 };
  const inp = { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13, padding: '7px 10px', boxSizing: 'border-box' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #1e3a5f', cursor: 'pointer' };

  return (
    <div style={ov}>
      <div style={bx}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: '#fff' }}>↺ Restart Skipped Exercises</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
          Select which skipped exercises to restore, then optionally add new or repeat completed ones.
        </div>

        {/* Skipped list */}
        <div style={{ marginBottom: 14 }}>
          <div style={lbl}>Skipped Exercises</div>
          {skipped.map((e, si) => {
            const ed = editableSkipped[si] || { editDur: e.duration||1, editReps: e.repetition||1 };
            return (
              <div key={e._localId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid #1e3a5f', flexWrap: 'wrap' }}>
                <input type="checkbox" checked={selected.includes(e._localId)} onChange={() => toggleSelected(e._localId)} />
                <span style={{ flex: 1, fontSize: 12, color: '#e2e8f0', minWidth: 80 }}>{e.name || 'Exercise'}</span>
                <input type="number" min={0} max={23} value={ed.editH}
                  onChange={ev => setEditableSkipped(prev => prev.map((x, j) => j === si ? { ...x, editH: Number(ev.target.value) } : x))}
                  style={{ width: 44, padding: '4px 4px', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 13, textAlign: 'center' }}
                />
                <span style={{ fontSize: 10, color: '#64748b' }}>h</span>
                <input type="number" min={0} max={59} value={ed.editM}
                  onChange={ev => setEditableSkipped(prev => prev.map((x, j) => j === si ? { ...x, editM: Number(ev.target.value) } : x))}
                  style={{ width: 44, padding: '4px 4px', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 13, textAlign: 'center' }}
                />
                <span style={{ fontSize: 10, color: '#64748b' }}>m</span>
                <input type="number" min={0} max={59} value={ed.editS}
                  onChange={ev => setEditableSkipped(prev => prev.map((x, j) => j === si ? { ...x, editS: Number(ev.target.value) } : x))}
                  style={{ width: 44, padding: '4px 4px', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 13, textAlign: 'center' }}
                />
                <span style={{ fontSize: 10, color: '#64748b' }}>s ×</span>
                <input type="number" min={1} max={50} value={ed.editReps}
                  onChange={ev => setEditableSkipped(prev => prev.map((x, j) => j === si ? { ...x, editReps: Number(ev.target.value) } : x))}
                  style={{ width: 44, padding: '4px 4px', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 13, textAlign: 'center' }}
                />
                <span style={{ fontSize: 10, color: '#64748b' }}>reps</span>
              </div>
            );
          })}
        </div>

        {/* Add from completed */}
        {completed.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ ...lbl, marginBottom: 0 }}>Add from Completed</div>
              <button onClick={() => setShowCompleted(p => !p)} style={{ fontSize: 11, padding: '2px 8px', background: showCompleted ? '#3b82f6' : '#334155', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {showCompleted ? '▲ Hide' : '▼ Show'}
              </button>
            </div>
            {showCompleted && completed.map(e => (
              <label key={e._localId} style={row}>
                <input type="checkbox" checked={pickedCompleted.includes(e._localId)} onChange={() => toggleCompleted(e._localId)} />
                <span style={{ flex: 1, fontSize: 13 }}>{e.name || 'Exercise'}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDur(e.duration || 1)} × {e.repetition || 1}</span>
              </label>
            ))}
          </div>
        )}

        {/* Add Unassigned exercises */}
        {(unassigned || []).length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ ...lbl, marginBottom: 0 }}>Add Unassigned Exercises</div>
              <button onClick={() => setShowUnassigned(p => !p)} style={{ fontSize: 11, padding: '2px 8px', background: showUnassigned ? '#3b82f6' : '#334155', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {showUnassigned ? '▲ Hide' : '▼ Show'}
              </button>
            </div>
            {showUnassigned && (unassigned || []).map((e, i) => (
              <label key={i} style={row}>
                <input type="checkbox" checked={pickedUnassigned.includes(i)} onChange={() => setPickedUnassigned(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])} />
                <span style={{ flex: 1, fontSize: 13 }}>{e.name || 'Exercise'}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDur(e.duration || 1)} × {e.repetition || 1}</span>
              </label>
            ))}
          </div>
        )}

        {/* Add new exercise */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ ...lbl, marginBottom: 0 }}>Add New Exercise</div>
            <button onClick={() => setShowAddNew(p => !p)} style={{ fontSize: 11, padding: '2px 8px', background: showAddNew ? '#3b82f6' : '#334155', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              {showAddNew ? '– Close' : '+ New'}
            </button>
          </div>
          {showAddNew && (
            <div style={{ background: '#0f172a', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <input style={{ ...inp, marginBottom: 8 }} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Exercise name..." />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {VARIANTS.map(v => (
                  <button key={v} onClick={() => setNewVariant(v)} style={{
                    padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                    background: newVariant === v ? '#3b82f6' : '#1e3a5f', color: newVariant === v ? '#fff' : '#94a3b8', border: 'none',
                  }}>{v}</button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input type="number" min={1} style={inp} value={newDuration} onChange={e => setNewDuration(e.target.value)} placeholder="Duration (min)" />
                <input type="number" min={1} style={inp} value={newLaps} onChange={e => setNewLaps(e.target.value)} placeholder="Laps/Reps" />
              </div>
              <button onClick={addNewExtra} style={{ width: '100%', padding: '8px 0', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                + Add
              </button>
            </div>
          )}
          {extras.map((ex, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#0f172a', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
              <span>{ex.name}</span>
              <button onClick={() => setExtras(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            onClick={handleRestart}
            disabled={total === 0}
            style={{ flex: 1, padding: '10px 0', background: total > 0 ? 'linear-gradient(135deg,#f59e0b,#ef4444)' : '#334155', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: total > 0 ? 'pointer' : 'default', fontSize: 13 }}
          >
            ↺ Restart ({selected.length + pickedCompleted.length} skipped{extras.length > 0 ? ` + ${extras.length} new` : ''})
          </button>
          <button onClick={onClose} style={{ padding: '10px 16px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
